<template>
    <div class="whatsapp-automation">
        <BackendHeader />

        <!-- Account and Group Selection - MOVED TO TOP -->
        <div class="account-section">
            <div class="section-header">
                <h2>WhatsApp Accounts</h2>
                <div class="section-header-actions">
                    <div class="header-controls">
                        <div class="engine-control-group">
                            <button 
                                class="button" 
                                :class="engineRunning ? 'button-danger' : 'button-success'"
                                :disabled="isStarting || isStopping" 
                                @click="toggleEngine">
                                {{ isStarting ? 'Starting...' : isStopping ? 'Stopping...' : engineRunning ? 'Stop Engine' : 'Start Engine' }}
                            </button>
                            <span v-if="engineRunning" class="status-indicator running">● Running</span>
                            <span v-else class="status-indicator stopped">● Stopped</span>
                        </div>
                        <label class="checkbox-label">
                            <input type="checkbox" v-model="showGroups" />
                            Show Groups
                        </label>
                        <button class="button button-success button-small" @click="initiateAddAccount"
                            title="Add New Account" :disabled="isCreatingAccount || !engineRunning">
                            {{ isCreatingAccount ? 'Creating...' : '+ Add Account' }}
                        </button>
                    </div>
                </div>
            </div>
            <div class="account-section-content">
                <!-- Accounts and Groups Grid -->
                <div v-if="accounts.length > 0" class="accounts-groups-grid">
                    <table class="accounts-groups-table">
                        <thead>
                            <tr>
                                <th>Account</th>
                                <th>Status</th>
                                <th v-if="showGroups">Groups (JIDs)</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="account in accounts" :key="account.id">
                                <td>
                                    <div class="account-display">
                                        <div v-if="account.name || account.phoneNumber" class="account-name">
                                            {{ account.name || account.phoneNumber }}
                                        </div>
                                        <div v-if="account.phoneNumber && account.name" class="account-phone">
                                            +{{ account.phoneNumber }}
                                        </div>
                                        <div v-else-if="!account.name && !account.phoneNumber"
                                            class="account-id-fallback">
                                            {{ account.id }}
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <span class="account-status-badge" :class="{
                                        'status-connected': account.status === 'connected',
                                        'status-connecting': account.status === 'connecting',
                                        'status-disconnected': account.status === 'disconnected'
                                    }">
                                        {{ account.status || 'disconnected' }}
                                    </span>
                                </td>
                                <td v-if="showGroups">
                                    <div v-if="account.groups && account.groups.length > 0" class="groups-jids-list">
                                        <div v-for="group in account.groups" :key="group.jid" class="group-jid-item">
                                            <span class="group-jid-text">{{ group.jid }}</span>
                                            <span class="group-subject-text" v-if="group.subject">({{ group.subject
                                                }})</span>
                                        </div>
                                    </div>
                                    <span v-else class="no-groups-text">No groups</span>
                                </td>
                                <td>
                                    <div class="account-actions-cell"
                                        style="display: flex; align-items: center; justify-content: flex-end; gap: 8px;">
                                        <button class="button-small button-icon button-icon-lighter"
                                            @click="refreshAccountGroups(account.id)"
                                            :disabled="isLoadingAccountGroups[account.id]" title="Refresh">
                                            <span class="material-symbols-outlined">refresh</span>
                                        </button>
                                        <div class="account-menu-container">
                                            <button class="button-small button-icon button-icon-lighter"
                                                @click.stop="toggleAccountMenu(account.id)" title="More options">
                                                ⋯
                                            </button>
                                            <div v-if="openAccountMenu === account.id" class="account-menu-dropdown"
                                                @click.stop>
                                                <button v-if="account.status === 'connected'" class="account-menu-item"
                                                    @click="disconnectAccount(account.id)">
                                                    Disconnect
                                                </button>
                                                <button v-if="account.status === 'disconnected'"
                                                    class="account-menu-item" @click="disconnectAccount(account.id)">
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div v-else class="no-accounts">
                    No accounts found. Click "+ Add Account" to create one.
                </div>
            </div>
        </div>

        <!-- Content Selection -->
        <div class="deal-selection-section" :class="{ 'section-disabled': !engineRunning }">
            <div class="section-header">
                <h2>Select Content</h2>
            </div>
            <div class="deal-selection-content">
                <ContentPicker 
                    v-model="selectedContent" 
                    @contentSelected="onContentSelected" 
                    :disabled="!engineRunning"
                    :allowed-content-types="['deal', 'list', 'giftList', 'outletItem']"
                    default-content-type="deal"
                />
            </div>
        </div>

        <!-- Configuration Management -->
        <div class="configuration-section" :class="{ 'section-disabled': !engineRunning }">
            <div class="section-header">
                <h2>Configurations</h2>
                <div class="section-header-actions">
                    <div class="configuration-selector" style="display: flex; align-items: center; gap: 8px;">
                        <select v-model="selectedConfig" :disabled="isLoadingConfiguration">
                            <option :value="null">-- Select Configuration --</option>
                            <option v-for="config in availableConfigurations" :key="config.id" :value="config">
                                {{ config.name }}
                            </option>
                        </select>
                        <button class="button-small button-primary" @click="addSelectedConfiguration"
                            :disabled="!selectedConfig || isLoadingConfiguration">
                            Add
                        </button>
                        <button class="button-small button-secondary" @click="editSelectedConfiguration"
                            :disabled="!selectedConfig || isLoadingConfiguration">
                            Edit
                        </button>
                        <button class="button-small button-danger" @click="deleteSelectedConfiguration"
                            :disabled="!selectedConfig || isLoadingConfiguration">
                            Delete
                        </button>
                    </div>
                    <div style="width: 1px; height: 24px; background-color: #ccc; margin: 0 8px;"></div>
                    <button class="button button-primary button-small" @click="openCreateConfigModal"
                        :disabled="!engineRunning">
                        New Configuration
                    </button>
                    <button class="button-small button-icon button-icon-lighter" @click="loadConfigurations"
                        title="Refresh">
                        <span class="material-symbols-outlined">refresh</span>
                    </button>
                </div>
            </div>
            <div class="configuration-section-content">
                <div v-if="openConfigurations.length > 0" class="global-apply-section">
                    <div class="global-apply-row">
                        <div class="global-apply-item">
                            <RichTextArea v-model="globalTagline" 
                                placeholder="Tagline to apply to all configurations (auto-updates)" 
                                :rows="1" 
                                @update:modelValue="(value) => updateAllTaglines(value)" />
                        </div>
                        <div class="global-apply-separator"></div>
                        <div class="global-apply-item global-apply-item-media">
                            <div class="global-media-wrapper">
                                <label class="global-media-label">Upload media file</label>
                                <div class="global-media-controls">
                                    <input ref="globalMediaFileInput" type="file" @change="handleGlobalMediaUpload"
                                        accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                        style="display: none" />
                                    <button type="button" class="button button-secondary button-xs"
                                        :disabled="isUploadingGlobalMedia" @click="triggerGlobalMediaFileInput">
                                        {{ isUploadingGlobalMedia ? 'Uploading...' : (globalMediaUrl ? 'Change' : 'Browse') }}
                                    </button>
                                    <div v-if="globalMediaUrl" class="global-media-preview-inline">
                                        <ImagePreview v-if="globalMediaIsImage" :imageUrl="globalMediaUrl" :maxWidth="40"
                                            :maxHeight="40" :finalWidth="300" :finalHeight="300" />
                                        <span v-else class="media-preview-text-inline">📄</span>
                                        <button type="button" class="button-small button-danger" @click="clearGlobalMedia">×</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button class="button button-primary button-small" 
                            :disabled="(!globalTagline && !globalMediaUrl) || isUploadingGlobalMedia"
                            @click="applyAllChanges">
                            Apply To All
                        </button>
                    </div>
                </div>
                <div v-if="openConfigurations.length > 0" class="open-configurations">
                    <div v-for="(config, index) in openConfigurations" :key="config.id" class="configuration-card">
                        <div class="configuration-card-header">
                            <h3>{{ config.name }}</h3>
                            <div class="configuration-card-actions">
                                <button class="button-small button-icon button-icon-lighter"
                                    @click="handleEditConfiguration(config)" title="Configuration Settings">
                                    <span class="material-symbols-outlined">menu</span>
                                </button>
                                <button class="button-small button-icon button-icon-lighter"
                                    @click="handleResetSelectedConfiguration(config)" title="Refresh">
                                    <span class="material-symbols-outlined">refresh</span>
                                </button>
                                <button class="button-small button-icon button-danger"
                                    @click="closeConfiguration(index)" title="Close Configuration">
                                    <span class="material-symbols-outlined">close</span>
                                </button>
                            </div>
                        </div>
                        <div class="configuration-card-content">
                            <div class="config-account-groups">
                                <div class="config-groups-header" @click="toggleConfigGroups(index)">
                                    <div class="config-groups-header-left">
                                        <span class="config-groups-toggle-icon"
                                            :class="{ 'expanded': configGroupsExpanded[index] }">▶</span>
                                        <strong>Groups ({{ handleGetConfigGroupsCount(config) }})</strong>
                                    </div>
                                    <div class="config-groups-header-pills">
                                        <span class="access-pill accessible">{{ handleGetAccessibleGroupsCount(config) }}
                                            accessible</span>
                                        <span class="access-pill not-accessible">{{ handleGetUnaccessibleGroupsCount(config)
                                            }} unaccessible</span>
                                    </div>
                                </div>
                                <div v-show="configGroupsExpanded[index]" class="config-groups-content">
                                    <table class="config-account-groups-table">
                                        <thead>
                                            <tr>
                                                <th>Group</th>
                                                <th>Access</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(group, idx) in config.groups"
                                                :key="idx">
                                                <td>
                                                    <div class="group-name-jid">
                                                        <div class="group-name">{{ group.subject || 'Unknown' }}</div>
                                                        <div class="group-jid">{{ group.jid }}</div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span v-if="hasGroupAccess(group.jid)"
                                                        class="access-indicator accessible">
                                                        ✅ Accessible
                                                    </span>
                                                    <span v-else class="access-indicator not-accessible">
                                                        ⚠️ No access
                                                    </span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <CollapsibleSection>
                                <template #title>Upload</template>
                                <template #header-extra>
                                    <div v-if="config.mediaUrl && config.mediaIsImage" class="config-header-image-preview">
                                        <img :src="config.mediaUrl" alt="Preview" />
                                    </div>
                                </template>
                                <div class="config-media-upload-controls">
                                    <input :ref="el => setConfigMediaFileInput(el, index)" type="file"
                                        @change="(e) => handleConfigMediaUpload(e, index)"
                                        accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                        style="display: none" />
                                    <button type="button" class="button button-secondary button-small"
                                        :disabled="config.isUploadingMedia" @click="triggerConfigMediaFileInput(index)">
                                        {{ config.isUploadingMedia ? 'Uploading...' : (config.mediaUrl ? 'Change Media'
                                        : 'Browse Media...') }}
                                    </button>
                                    <div v-if="config.mediaUrl" class="config-media-preview">
                                        <ImagePreview v-if="config.mediaIsImage" :imageUrl="config.mediaUrl"
                                            :maxWidth="100" :maxHeight="100" :finalWidth="300" :finalHeight="300" />
                                        <span v-else class="media-preview-text">📄 Document</span>
                                        <button type="button" class="button-small button-danger"
                                            @click="clearConfigMedia(index)">
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            </CollapsibleSection>
                           <!-- 
                            <CollapsibleSection>
                                <template #title>Tagline</template>
                                <RichTextArea v-model="config.taglineOverride"
                                    @update:modelValue="(value) => updateConfigTagline(index, value || '')"
                                    placeholder="Override tagline for this deal" :rows="2" />
                            </CollapsibleSection>
                        -->
                            <CollapsibleSection>
                                <template #title>Message</template>
                                <template #header-extra>
                                    <div class="template-selector-in-header">
                                    <select 
                                            v-model="config.selectedTemplateName" 
                                            @change="onTemplateChange(index, config.selectedTemplateName)"
                                            class="template-select"
                                            style="width: 100%; padding: 6px; border: 1px solid #ddd; border-radius: 4px;"
                                        >
                                            <option 
                                                v-for="template in config.messageTemplates || []" 
                                                :key="template.id || template.name" 
                                                :value="template.name"
                                            >
                                                {{ template.name }}
                                            </option>
                                            <option v-if="!config.messageTemplates || config.messageTemplates.length === 0" value="default">
                                                Default
                                            </option>
                                        </select>
                                    <label class="checkbox-label">
                                        <input type="checkbox" v-model="config.skipPreview" />
                                        No Link Preview
                                    </label>
                                </div>
                                </template>
                                <div class="config-message-editor">
                                    <RichTextArea 
                                        v-model="config.displayMessage"
                                        @update:modelValue="(value) => updateConfigMessage(index, value || '')"
                                        :rows="6"
                                        placeholder="Edit message (final text will be shown)" />
                                </div>
                            </CollapsibleSection>
                            <div class="config-message-preview">
                                <div class="whatsapp-preview-container">
                                    <div class="whatsapp-preview-message">
                                        <div class="whatsapp-bubble">
                                            <!-- Show uploaded media image if available (replaces link preview) -->
                                            <div 
                                                v-if="config.mediaUrl && config.mediaIsImage"
                                                class="whatsapp-image-preview"
                                            >
                                                <img 
                                                    :src="config.mediaUrl" 
                                                    alt="Image preview" 
                                                />
                                            </div>
                                            <!-- Show link preview only if no media image and conditions are met -->
                                            <div 
                                                v-else-if="!config.skipPreview && getMessageUrl(config.displayMessage || config.generatedMessage) && selectedContent"
                                                class="whatsapp-link-preview"
                                            >
                                                <div class="link-preview-image" v-if="getContentImageUrl(selectedContent)">
                                                    <img :src="getContentImageUrl(selectedContent)" :alt="getContentTitle(selectedContent)" />
                                                </div>
                                                <div class="link-preview-content">
                                                    <div class="link-preview-title">{{ getContentTitle(selectedContent) || 'Content' }}
                                                    </div>
                                                    <div class="link-preview-description"
                                                        v-if="getContentDescription(selectedContent)">
                                                        {{ getContentDescription(selectedContent).substring(0, 100) }}{{
                                                        getContentDescription(selectedContent).length > 100 ?
                                                        '...' : '' }}
                                                    </div>
                                                    <div class="link-preview-url">{{ getMessageUrl(config.displayMessage
                                                        || config.generatedMessage)
                                                        }}</div>
                                                </div>
                                            </div>
                                            <div 
                                                class="whatsapp-message-text"
                                                :class="{ 'has-image-preview': config.mediaUrl && config.mediaIsImage }"
                                                v-html="formatWhatsAppMessage(config.displayMessage || config.generatedMessage || 'Select content to see preview')">
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="config-actions">
                                <div class="config-schedule-controls">
                                    <select v-model="config.sendMode" class="schedule-mode-select">
                                        <option value="now">Now</option>
                                        <option value="schedule">Schedule</option>
                                        <option value="delay">Delay</option>
                                    </select>
                                    <input v-if="config.sendMode === 'schedule'" v-model="config.scheduledDateTime"
                                        type="datetime-local" class="schedule-datetime-input" :min="getMinDateTime()" />
                                    <div v-if="config.sendMode === 'delay'" class="delay-inputs" style="display: flex; gap: 8px; align-items: center;">
                                        <input v-model.number="config.delayHours" type="number" min="0" max="23" 
                                            placeholder="Hours" class="delay-input" style="width: 80px;" />
                                        <span>:</span>
                                        <input v-model.number="config.delayMinutes" type="number" min="0" max="59" 
                                            placeholder="Minutes" class="delay-input" style="width: 80px;" />
                                        <span>:</span>
                                        <input v-model.number="config.delaySeconds" type="number" min="0" max="59" 
                                            placeholder="Seconds" class="delay-input" style="width: 80px;" />
                                    </div>
                                    <button class="button button-primary"
                                        :disabled="!selectedContent || !config.generatedMessage || (config.sendMode === 'schedule' && !config.scheduledDateTime) || config.isSendingToQueue"
                                        @click="sendConfigurationToQueue(index)">
                                        {{ config.isSendingToQueue ? 'Sending...' : (config.sendMode === 'schedule' ?
                                        'Schedule' : config.sendMode === 'delay' ? 'Add with Delay' : 'Send to Queue')
                                        }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>


        <!-- Queue Tabs -->
        <div :class="{ 'section-disabled': !engineRunning }">
            <Tabs :tabs="tabs" initialTab="queue-status" @tab-change="handleTabChange">
            <!-- Header Section (right of tabs) -->
            <template #header="{ activeTab }">
                <div v-if="isStatusUpdating && !isStoppingQueue" class="status-update-spinner-inline">
                    <span class="spinner-icon">⟳</span>
                    <span class="countdown-text">{{ statusUpdateCountdown }}s</span>
                </div>
                <!-- Queue controls - always visible -->
                <button v-if="!queueProcessingEnabled && engineRunning"
                        class="button-small button-icon button-success"
                        :disabled="isStartingQueue || isStoppingQueue"
                        @click="startQueue"
                        title="Start Queue Processing">
                    <span class="material-symbols-outlined">play_arrow</span>
                    <span>{{ isStartingQueue ? 'Starting...' : 'Start Queue Processing' }}</span>
                </button>
                <div v-if="queueProcessingEnabled" style="display: inline-flex; align-items: center; gap: 8px;">
                    <button class="button-small button-icon button-danger"
                            :disabled="isStoppingQueue"
                            @click="stopQueue"
                            title="Stop Queue">
                        <span class="material-symbols-outlined">pause</span>
                        <span>{{ isStoppingQueue ? 'Stopping...' : 'Stop Queue' }}</span>
                    </button>
                </div>
            </template>

            <!-- Queue Status Header -->
            <template #tab-header-queue-status="{ tab }">
                <span>Queue Status ({{ queueItemsCount }})</span>
                <span v-if="queueProcessingEnabled" class="status-indicator running">● Processing</span>
                <span v-else-if="engineRunning" class="status-indicator stopped">● Paused</span>
            </template>

            <!-- Queue Status Content -->
            <template #tab-panel-queue-status>
                <div class="queue-list">
                    <!-- Pagination for Queue Status -->
                    <div v-if="queueItemsList.length > 0" class="pagination-controls-top">
                        <button 
                            class="button-xs" 
                            :disabled="queuePage === 1"
                            @click="goToQueuePage(queuePage - 1)">
                            Previous
                        </button>
                        <span class="pagination-info-small">
                            Page {{ queuePage }} of {{ queueTotalPages }}
                        </span>
                        <button 
                            class="button-xs" 
                            :disabled="queuePage >= queueTotalPages"
                            @click="goToQueuePage(queuePage + 1)">
                            Next
                        </button>
                        <button 
                            class="button-small button-icon button-icon-lighter" 
                            @click="handleClearTab('queue-status')" 
                            :title="getClearButtonTitle('queue-status')">
                            <span class="material-symbols-outlined">delete_outline</span>
                            <span>Clear</span>
                        </button>
                    </div>
                    <table v-if="queueItemsList.length > 0" class="queue-table">
                        <thead>
                            <tr>
                                <th>Account</th>
                                <th>Group</th>
                                <th>Message Preview</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, idx) in paginatedQueueItems" :key="idx">
                                <td>
                                    <div class="account-display">
                                        <div v-if="item.accountName || item.accountPhoneNumber" class="account-name">
                                            {{ item.accountName }}
                                        </div>
                                        <div v-if="item.accountPhoneNumber && item.accountName" class="account-phone">
                                            +{{ item.accountPhoneNumber }}
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <div class="account-display">
                                        <div class="account-name">
                                            <div class="group-name-jid">
                                                <div >{{ item.groupSubject }}</div>
                                                <div >{{ item.groupName }}</div>
                                            </div>
                                        </div>
                                        <div class="group-jid">
                                            {{ item.configurationName || '-' }}
                                        </div>
                                    </div>
                                    
                                </td>
                               
                                <td class="message-preview-cell">{{ item.message.substring(0, 100) }}{{
                                    item.message.length >
                                    100 ? '...' : '' }}</td>
                                <td>
                                    <span class="status-badge" :class="{
                                        'status-pending': item.status === 'pending',
                                        'status-processing': item.status === 'processing',
                                        'status-sent': item.status === 'sent',
                                        'status-failed': item.status === 'failed'
                                    }">
                                        {{ item.status ? item.status.toUpperCase() : 'PENDING' }}
                                    </span>
                                </td>
                                <td>
                                    <button 
                                        class="button-small button-danger" 
                                        :disabled="item.status === 'processing' || removingFromQueue[`${item.accountId}-${item.id}`]"
                                        @click="removeFromQueue(item.accountId, item.id)">
                                        <span v-if="removingFromQueue[`${item.accountId}-${item.id}`]" class="remove-button-spinner">
                                            <span class="material-symbols-outlined">sync</span>
                                        </span>
                                        <span v-else>Remove</span>
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-else class="no-queue">
                        No items in queue
                    </div>
                </div>
            </template>

            <!-- Scheduled Messages Header -->
            <template #tab-header-scheduled-messages="{ tab }">
                <span>Scheduled Messages ({{ scheduledItemsCount }})</span>
            </template>

            <!-- Scheduled Messages Content -->
            <template #tab-panel-scheduled-messages>
                <div class="queue-list">
                    <!-- Pagination for Scheduled Messages -->
                    <div v-if="scheduledItemsList.length > 0" class="pagination-controls-top">
                        <button 
                            class="button-xs" 
                            :disabled="scheduledPage === 1"
                            @click="goToScheduledPage(scheduledPage - 1)">
                            Previous
                        </button>
                        <span class="pagination-info-small">
                            Page {{ scheduledPage }} of {{ scheduledTotalPages }}
                        </span>
                        <button 
                            class="button-xs" 
                            :disabled="scheduledPage >= scheduledTotalPages"
                            @click="goToScheduledPage(scheduledPage + 1)">
                            Next
                        </button>
                        <button 
                            class="button-small button-icon button-icon-lighter" 
                            @click="handleClearTab('scheduled-messages')" 
                            :title="getClearButtonTitle('scheduled-messages')">
                            <span class="material-symbols-outlined">delete_outline</span>
                            <span>Clear</span>
                        </button>
                    </div>
                    <table v-if="scheduledItemsList.length > 0" class="queue-table">
                        <thead>
                            <tr>
                                <th>Configuration</th>
                                <th>Account</th>
                                <th>Group</th>
                                <th>Message Preview</th>
                                <th>Scheduled Date/Time (ET)</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, idx) in paginatedScheduledItems" :key="idx" class="scheduled-message-row">
                                <td class="scheduled-cell-small">{{ item.configurationName || '-' }}</td>
                                <td class="scheduled-cell-small">
                                    <div class="account-display">
                                        <div v-if="item.accountName || item.accountPhoneNumber" class="account-name">
                                            {{ item.accountName }}
                                        </div>
                                        <div v-if="item.accountPhoneNumber && item.accountName" class="account-phone">
                                            +{{ item.accountPhoneNumber }}
                                        </div>
                                    </div>
                                </td>
                                <td class="scheduled-cell-small">
                                    <div class="group-name-jid">
                                        <div class="group-name">{{ item.groupSubject }}</div>
                                        <div class="group-jid">{{ item.groupName }}</div>
                                    </div>
                                </td>
                                <td class="message-preview-cell scheduled-cell-small">{{ item.message.substring(0, 100) }}{{
                                    item.message.length >
                                    100 ? '...' : '' }}</td>
                                <td class="scheduled-cell-small">
                                    <div class="scheduled-datetime-wrapper">
                                        <div class="scheduled-datetime-input-group">
                                            <input 
                                                type="datetime-local" 
                                                v-model="item.scheduledAtLocal"
                                                class="scheduled-datetime-input-small"
                                                :min="getMinDateTime()" />
                                            <button 
                                                class="button-xs button-primary save-datetime-button"
                                                @click="saveScheduledTime(item.accountId, item.id)"
                                                title="Save scheduled time">
                                                Save
                                            </button>
                                        </div>
                                        <div class="countdown-text">{{ getCountdownForItem(item.scheduledAt) }}</div>
                                    </div>
                                </td>
                                <td class="scheduled-cell-small">
                                    <span class="status-badge" :class="{
                                        'status-scheduled': item.status === 'scheduled',
                                        'status-pending': item.status === 'pending'
                                    }">
                                        {{ item.status ? item.status.toUpperCase() : 'SCHEDULED' }}
                                    </span>
                                </td>
                                <td class="scheduled-cell-small">
                                    <button 
                                        class="button-xs button-primary"
                                        @click="postScheduledMessageNow(item.accountId, item.id)">
                                        Post Now
                                    </button>
                                    <button 
                                        class="button-xs button-danger"
                                        @click="removeMessage(item.accountId, item.id)">
                                        Remove
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-else class="no-queue">
                        No scheduled messages
                    </div>
                </div>
            </template>

            <!-- Sent Messages Header -->
            <template #tab-header-sent-messages="{ tab }">
                <span>Sent Messages ({{ sentItemsCount }})</span>
            </template>

            <!-- Sent Messages Content -->
            <template #tab-panel-sent-messages>
                <div class="queue-list">
                    <!-- Pagination for Sent Messages -->
                    <div v-if="sentItemsList.length > 0" class="pagination-controls-top">
                        <button 
                            class="button-xs" 
                            :disabled="sentPage === 1"
                            @click="goToSentPage(sentPage - 1)">
                            Previous
                        </button>
                        <span class="pagination-info-small">
                            Page {{ sentPage }} of {{ sentTotalPages }}
                        </span>
                        <button 
                            class="button-xs" 
                            :disabled="sentPage >= sentTotalPages"
                            @click="goToSentPage(sentPage + 1)">
                            Next
                        </button>
                        <button 
                            class="button-small button-icon button-icon-lighter" 
                            @click="handleClearTab('sent-messages')" 
                            :title="getClearButtonTitle('sent-messages')">
                            <span class="material-symbols-outlined">delete_outline</span>
                            <span>Clear</span>
                        </button>
                    </div>
                    <table v-if="sentItemsList.length > 0" class="queue-table">
                        <thead>
                            <tr>
                                <th>Configuration</th>
                                <th>Account</th>
                                <th>Group</th>
                                <th>Message Preview</th>
                                <th>Status</th>
                                <th>Sent Date/Time</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, idx) in paginatedSentItems" :key="idx">
                                <td>{{ item.configurationName || '-' }}</td>
                                <td>
                                    <div class="account-display">
                                        <div v-if="item.accountName || item.accountPhoneNumber" class="account-name">
                                            {{ item.accountName }}
                                        </div>
                                        <div v-if="item.accountPhoneNumber && item.accountName" class="account-phone">
                                            +{{ item.accountPhoneNumber }}
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <div class="group-name-jid">
                                        <div class="group-name">{{ item.groupSubject }}</div>
                                        <div class="group-jid">{{ item.groupName }}</div>
                                    </div>
                                </td>
                                <td class="message-preview-cell">{{ item.message.substring(0, 100) }}{{
                                    item.message.length >
                                    100 ? '...' : '' }}</td>
                                <td>
                                    <span :class="{
                                        'status-badge status-sent': item.status === 'sent',
                                        'status-badge status-sending': item.status === 'sending'
                                    }">
                                        {{ item.status.toUpperCase() }}
                                    </span>
                                </td>
                                <td>
                                    <div v-if="item.sentAt">{{ formatDateTime(item.sentAt) }}</div>
                                    <div v-else>-</div>
                                    <div v-if="item.whatsAppMessageId" class="message-id-display" style="font-size: 0.85em; color: #666; margin-top: 4px;">
                                        ID: {{ item.whatsAppMessageId }}
                                    </div>
                                </td>
                                <td>
                                    <button v-if="item.status === 'sent'"
                                        class="button-small button-primary"
                                        @click="reAddToQueue(item.accountId, item.id)">
                                        Re-Add To Queue
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-else class="no-queue">
                        No sent messages yet
                    </div>
                </div>
            </template>

            <!-- Delivered Messages Tab Header -->
            <template #tab-header-delivered-messages="{ tab }">
                <span>Delivered Messages ({{ deliveredItemsCount }})</span>
            </template>

            <!-- Delivered Messages Content -->
            <template #tab-panel-delivered-messages>
                <div class="queue-list">
                    <!-- Pagination for Delivered Messages -->
                    <div v-if="deliveredItemsList.length > 0" class="pagination-controls-top">
                        <button 
                            class="button-xs" 
                            :disabled="deliveredPage === 1"
                            @click="goToDeliveredPage(deliveredPage - 1)">
                            Previous
                        </button>
                        <span class="pagination-info-small">
                            Page {{ deliveredPage }} of {{ deliveredTotalPages }}
                        </span>
                        <button 
                            class="button-xs" 
                            :disabled="deliveredPage >= deliveredTotalPages"
                            @click="goToDeliveredPage(deliveredPage + 1)">
                            Next
                        </button>
                        <button 
                            class="button-small button-icon button-icon-lighter" 
                            @click="handleClearTab('delivered-messages')" 
                            :title="getClearButtonTitle('delivered-messages')">
                            <span class="material-symbols-outlined">delete_outline</span>
                            <span>Clear</span>
                        </button>
                    </div>
                    <table v-if="deliveredItemsList.length > 0" class="queue-table">
                        <thead>
                            <tr>
                                <th>Configuration</th>
                                <th>Account</th>
                                <th>Group</th>
                                <th>Message Preview</th>
                                <th>Status</th>
                                <th>Sent Date/Time</th>
                                <th>Delivered Date/Time</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, idx) in paginatedDeliveredItems" :key="idx">
                                <td>{{ item.configurationName || '-' }}</td>
                                <td>
                                    <div class="account-display">
                                        <div v-if="item.accountName || item.accountPhoneNumber" class="account-name">
                                            {{ item.accountName }}
                                        </div>
                                        <div v-if="item.accountPhoneNumber && item.accountName" class="account-phone">
                                            +{{ item.accountPhoneNumber }}
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <div class="group-name-jid">
                                        <div class="group-name">{{ item.groupSubject }}</div>
                                        <div class="group-jid">{{ item.groupName }}</div>
                                    </div>
                                </td>
                                <td class="message-preview-cell">{{ item.message.substring(0, 100) }}{{
                                    item.message.length >
                                    100 ? '...' : '' }}</td>
                                <td>
                                    <span :class="{
                                        'status-badge status-delivered': item.status === 'delivered'
                                    }">
                                        {{ item.status.toUpperCase() }}
                                    </span>
                                    <div v-if="item.errorMessage" class="error-message-text">{{ item.errorMessage }}
                                    </div>
                                </td>
                                <td>{{ item.sentAt ? formatDateTime(item.sentAt) : '-' }}</td>
                                <td>{{ item.deliveredAt ? formatDateTime(item.deliveredAt) : '-' }}</td>
                                <td>
                                    <button v-if="item.status === 'delivered'"
                                        class="button-small button-primary"
                                        @click="reAddToQueue(item.accountId, item.id)">
                                        Re-Add To Queue
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-else class="no-queue">
                        No delivered messages yet
                    </div>
                </div>
            </template>

            <!-- Failed Messages Tab Header -->
            <template #tab-header-failed-messages="{ tab }">
                <span>Failed Messages ({{ failedItemsCount }})</span>
            </template>

            <!-- Failed Messages Content -->
            <template #tab-panel-failed-messages>
                <div class="queue-list">
                    <!-- Pagination for Failed Messages -->
                    <div v-if="failedItemsList.length > 0" class="pagination-controls-top">
                        <button 
                            class="button-xs" 
                            :disabled="failedPage === 1"
                            @click="goToFailedPage(failedPage - 1)">
                            Previous
                        </button>
                        <span class="pagination-info-small">
                            Page {{ failedPage }} of {{ failedTotalPages }}
                        </span>
                        <button 
                            class="button-xs" 
                            :disabled="failedPage >= failedTotalPages"
                            @click="goToFailedPage(failedPage + 1)">
                            Next
                        </button>
                        <button 
                            class="button-small button-icon button-icon-lighter" 
                            @click="handleClearTab('failed-messages')" 
                            :title="getClearButtonTitle('failed-messages')">
                            <span class="material-symbols-outlined">delete_outline</span>
                            <span>Clear</span>
                        </button>
                    </div>
                    <table v-if="failedItemsList.length > 0" class="queue-table">
                        <thead>
                            <tr>
                                <th>Configuration</th>
                                <th>Account</th>
                                <th>Group</th>
                                <th>Message Preview</th>
                                <th>Status</th>
                                <th>Sent Date/Time</th>
                                <th>Failed Date/Time</th>
                                <th>Error Message</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, idx) in paginatedFailedItems" :key="idx">
                                <td>{{ item.configurationName || '-' }}</td>
                                <td>
                                    <div class="account-display">
                                        <div v-if="item.accountName || item.accountPhoneNumber" class="account-name">
                                            {{ item.accountName }}
                                        </div>
                                        <div v-if="item.accountPhoneNumber && item.accountName" class="account-phone">
                                            +{{ item.accountPhoneNumber }}
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <div class="group-name-jid">
                                        <div class="group-name">{{ item.groupSubject }}</div>
                                        <div class="group-jid">{{ item.groupName }}</div>
                                    </div>
                                </td>
                                <td class="message-preview-cell">{{ item.message.substring(0, 100) }}{{
                                    item.message.length >
                                    100 ? '...' : '' }}</td>
                                <td>
                                    <span :class="{
                                        'status-badge status-failed': item.status === 'failed'
                                    }">
                                        {{ item.status.toUpperCase() }}
                                    </span>
                                </td>
                                <td>{{ item.sentAt ? formatDateTime(item.sentAt) : '-' }}</td>
                                <td>{{ item.failedAt ? formatDateTime(item.failedAt) : '-' }}</td>
                                <td>
                                    <div v-if="item.errorMessage" class="error-message-text">{{ item.errorMessage }}
                                    </div>
                                    <span v-else>-</span>
                                </td>
                                <td>
                                    <div style="display: flex; gap: 8px;">
                                        <button v-if="item.status === 'failed'"
                                            class="button-small button-primary"
                                            @click="checkMessageStatus(item.accountId, item.id)">
                                            Check Status
                                        </button>
                                        <button v-if="item.status === 'failed'"
                                            class="button-small button-primary"
                                            @click="reAddToQueue(item.accountId, item.id)">
                                            Re-Add To Queue
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-else class="no-queue">
                        No failed messages yet
                    </div>
                </div>
            </template>
        </Tabs>
        </div>

        <!-- Create/Edit Configuration Modal -->
        <div v-if="showCreateConfigModal" class="modal-overlay">
            <div class="modal-content modal-large">
                <div class="modal-header">
                    <h2>{{ editingConfigIndex !== null ? 'Edit Configuration' : 'New Configuration' }}</h2>
                    <button class="modal-close" @click="closeCreateConfigModal">&times;</button>
                </div>
                <div class="modal-body">
                    <div class="config-form">
                        <div class="form-group">
                            <label>Configuration Name:</label>
                            <input v-model="editingConfig.name" type="text" placeholder="Enter configuration name"
                                class="form-input" />
                        </div>

                        <div class="form-group">
                            <label>Groups:</label>
                            <div class="config-account-selection">
                                <div class="header-controls" style="display: flex; align-items: center; gap: 8px;">
                                    <div class="dropdown-group" style="flex: 1; min-width: 200px;">
                                        <SearchableDropdown
                                            v-model="newConfigSelectedGroup"
                                            :list="allGroups"
                                            :option-key="(group: GroupInfo) => group.jid"
                                            :option-label="(group: GroupInfo) => `${group.subject} (${group.size} members)`"
                                            :options="{
                                                sortBy: (group: GroupInfo) => group.subject.toLowerCase(),
                                                searchable: true
                                            }"
                                            placeholder="Select Group"
                                        >
                                            <template #option="{ option }">
                                                {{ option.subject }} ({{ option.size }} members)
                                            </template>
                                        </SearchableDropdown>
                                    </div>
                                    <button class="button button-primary button-small"
                                        :disabled="!newConfigSelectedGroup" @click="handleAddGroupToConfig(newConfigSelectedGroup)">
                                        Add Group
                                    </button>
                                </div>

                                <div v-if="editingConfig.groups.length > 0" class="config-groups-list">
                                    <table class="config-account-groups-table">
                                        <thead>
                                            <tr>
                                                <th>Group</th>
                                                <th>Access</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(group, index) in editingConfig.groups" :key="index">
                                                <td>
                                                    <div class="group-name-jid">
                                                        <div class="group-name">{{ group.subject }}</div>
                                                        <div class="group-jid">{{ group.jid }}</div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span v-if="hasGroupAccess(group.jid)" class="access-indicator accessible">
                                                        ✅ Accessible
                                                    </span>
                                                    <span v-else class="access-indicator not-accessible">
                                                        ⚠️ No access
                                                    </span>
                                                </td>
                                                <td>
                                                    <button class="button-small button-danger"
                                                        @click="handleRemoveGroupFromConfig(group)">
                                                        Remove
                                                    </button>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div v-else class="no-groups">
                                    No groups added yet. Select a group above, then click Add Group.
                                </div>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>Message Templates:</label>
                            <div class="templates-management">
                                <div class="form-hint" style="margin-bottom: 10px;">
                                    Available placeholders: &#123;&#123;id&#125;&#125;, &#123;&#123;dealId&#125;&#125;, &#123;&#123;title&#125;&#125;,
                                    &#123;&#123;description&#125;&#125;, &#123;&#123;price&#125;&#125;,
                                    &#123;&#123;retailPrice&#125;&#125;, &#123;&#123;pricing&#125;&#125;,
                                    &#123;&#123;discount&#125;&#125;, &#123;&#123;url&#125;&#125;,
                                    &#123;&#123;tagline&#125;&#125;,
                                    &#123;&#123;imageUrl&#125;&#125;, &#123;&#123;uniqueId&#125;&#125;,
                                    &#123;&#123;fullSlug&#125;&#125;
                                </div>
                                
                                <div class="template-selector-row">
                                    <div class="template-dropdown-wrapper">
                                        <select 
                                            v-model="selectedTemplateIndex"
                                            class="form-input template-dropdown">
                                            <option :value="null">-- Select Template --</option>
                                            <option 
                                                v-for="(template, index) in editingConfig.messageTemplates || []" 
                                                :key="template.id || index"
                                                :value="index">
                                                {{ template.name }}
                                            </option>
                                        </select>
                                    </div>
                                    <button 
                                        type="button"
                                        class="button button-primary button-small template-add-btn" 
                                        @click="addNewTemplate"
                                        title="Add new template">
                                        +
                                    </button>
                                </div>

                                <div v-if="selectedTemplateIndex !== null && editingConfig.messageTemplates && editingConfig.messageTemplates[selectedTemplateIndex]" class="template-editor">
                                    <div class="template-editor-header">
                                        <div class="template-name-input">
                                            <label>Template Name:</label>
                                            <input 
                                                v-model="editingConfig.messageTemplates[selectedTemplateIndex].name" 
                                                type="text" 
                                                placeholder="Template name"
                                                class="form-input template-name-field"
                                                @blur="ensureTemplateName(editingConfig.messageTemplates[selectedTemplateIndex] as MessageTemplate, selectedTemplateIndex)" />
                                        </div>
                                        <button 
                                            type="button"
                                            class="button-small button-danger" 
                                            @click="removeTemplate(selectedTemplateIndex)"
                                            title="Delete template">
                                            Delete
                                        </button>
                                    </div>
                                    <div class="template-content">
                                        <label>Template Content:</label>
                                        <textarea 
                                            v-model="editingConfig.messageTemplates[selectedTemplateIndex].content"
                                            placeholder="Enter message template with placeholders..."
                                            class="form-textarea template-content-field" 
                                            rows="6"></textarea>
                                    </div>
                                    <div class="template-options">
                                        <label class="checkbox-label">
                                            <input 
                                                type="checkbox" 
                                                v-model="editingConfig.messageTemplates[selectedTemplateIndex].useContentImage" />
                                            Use Content Image as Upload Image
                                        </label>
                                    </div>
                                </div>

                                <div v-else-if="!editingConfig.messageTemplates || editingConfig.messageTemplates.length === 0" class="no-templates">
                                    <p>No templates added yet. Click the "+" button to create your first template.</p>
                                    <div v-if="editingConfig.defaultMessage" class="default-message-fallback">
                                        <p><strong>Note:</strong> The default message below will be used if no templates are defined:</p>
                                        <textarea 
                                            v-model="editingConfig.defaultMessage"
                                            placeholder="Enter default message template..."
                                            class="form-textarea" 
                                            rows="4"
                                            readonly></textarea>
                                    </div>
                                </div>
                                <div v-else class="no-template-selected">
                                    <p>Select a template from the dropdown above to edit, or click "+" to add a new template.</p>
                                </div>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>Unique ID Prefix:</label>
                            <input v-model="editingConfig.uniqueIdPrefix" type="text" placeholder="e.g., DEAL-"
                                class="form-input" />
                            <div class="form-hint">
                                When using <span v-pre>{{ uniqueId }}</span> placeholder, it will be replaced with this prefix + (group index + 1).
                                For example, if prefix is "DEAL-" and group is 2nd in the list, it becomes "DEAL-2".
                            </div>
                        </div>

                        <div class="form-group">
                            <label>Default Tag Line:</label>
                            <RichTextArea v-model="editingConfig.defaultTagLine" placeholder="e.g., Great Deal!" :rows="2" />
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <div class="modal-actions">
                        <button class="button button-primary" :disabled="!canSaveConfig || isSavingConfiguration"
                            @click="saveConfiguration">
                            {{ isSavingConfiguration ? 'Saving...' : 'Save Configuration' }}
                        </button>
                        <button class="button button-secondary" @click="closeCreateConfigModal">
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Add Account Modal -->
        <div v-if="showAddAccountModal" class="modal-overlay" @click.self="closeAddAccountModal">
            <div class="modal-content">
                <div class="modal-header">
                    <h2>Add WhatsApp Account</h2>
                    <button class="modal-close" @click="closeAddAccountModal">&times;</button>
                </div>
                <div class="modal-body">
                    <div v-if="!qrCode" class="loading-section">
                        <div class="loading-spinner"></div>
                        <p>{{ isCreatingAccount ? 'Generating QR code...' : 'Waiting for QR code...' }}</p>
                        <div v-if="newAccountId" class="account-id-display">
                            <strong>Account ID:</strong> <span class="account-id-value">{{ newAccountId }}</span>
                        </div>
                    </div>

                    <div v-if="qrCode" class="qr-section">
                        <div v-if="newAccountId" class="account-id-display">
                            <strong>Account ID:</strong> <span class="account-id-value">{{ newAccountId }}</span>
                        </div>
                        <h3>Scan QR Code with WhatsApp</h3>
                        <p class="qr-instructions">
                            1. Open WhatsApp on your phone<br>
                            2. Go to Settings → Linked Devices<br>
                            3. Tap "Link a Device"<br>
                            4. Scan this QR code
                        </p>
                        <div class="qr-code-container">
                            <img v-if="qrCodeImageUrl" :src="qrCodeImageUrl" alt="QR Code" class="qr-code-image"
                                @error="handleQRImageError" @load="handleQRImageLoad" />
                            <div v-else class="qr-error">
                                <p>QR code data: {{ qrCode ? qrCode.substring(0, 50) + '...' : 'No QR code' }}</p>
                                <p>Generating image...</p>
                            </div>
                        </div>
                        <div class="qr-status">
                            <span v-if="accountStatus === 'connecting'" class="status-connecting">
                                ⏳ Waiting for scan...
                            </span>
                            <span v-else-if="accountStatus === 'connected'" class="status-connected">
                                ✅ Account connected successfully!
                            </span>
                            <span v-else class="status-disconnected">
                                ⚠️ Not connected
                            </span>
                        </div>
                        <div class="modal-actions">
                            <button class="button button-success" v-if="accountStatus === 'connected'"
                                @click="handleAccountConnected">
                                Done
                            </button>
                            <button class="button button-secondary" @click="closeAddAccountModal">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onUnmounted, nextTick, watch, type Ref } from 'vue'
import BackendHeader from '@/components/BackendHeader.vue'
import ContentPicker from '@/components/ContentPicker.vue'
import RichTextArea from '@/components/RichTextArea.vue'
import ImagePreview from '@/components/Backend/ImagePreview.vue'
import CollapsibleSection from '@/components/Backend/CollapsibleSection.vue'
import SearchableDropdown from '@/components/Backend/SearchableDropdown.vue'
import Tabs from '@/components/Tabs.vue'
import api from '@/api'
import {
    GroupInfo,
    QueueStatusResponse,
    WhatsAppConfiguration,
    WhatsAppMessageDto,
    MessageType,
    MessageTemplate
} from '@/classes/WhatsApp'
import { DealDetails } from '@/classes/DealDetails'
import { Article } from '@/classes/Articles'
import { OutletItem } from '@/classes/OutletItem'
import { replacePlaceholders } from '@/helpers/whatsappPlaceholders'
import util from '@/helpers/util'
import { useWhatsAppAccounts, type AccountWithStatus } from '@/composables/useWhatsAppAccounts'
import { useWhatsAppEngine } from '@/composables/useWhatsAppEngine'
import { useWhatsAppQueue } from '@/composables/useWhatsAppQueue'
import { useWhatsAppMessages } from '@/composables/useWhatsAppMessages'
import { useWhatsAppConfigurations, type ConfigWithExtras } from '@/composables/useWhatsAppConfigurations'
import { useWhatsAppMedia } from '@/composables/useWhatsAppMedia'
import { useWhatsAppMessageGeneration } from '@/composables/useWhatsAppMessageGeneration'
import { getMinDateTime, convertEasternToUTC, convertUTCToEasternDateTimeLocal, formatDateTime } from '@/utils/whatsAppTimezone'

interface SelectedGroup {
    accountId: string
    groupJid: string
}

export default defineComponent({
    name: 'WhatsAppAutomation',
    components: {
        BackendHeader,
        ContentPicker,
        RichTextArea,
        ImagePreview,
        CollapsibleSection,
        Tabs,
        SearchableDropdown
    },
    setup() {
        // Account Management Composable
        const {
            accounts,
            allGroups,
            isLoadingAccounts,
            openAccountMenu,
            loadAccounts,
            createAccount: createAccountComposable,
            disconnectAccount,
            checkAccountStatus,
            refreshAccountGroups,
            toggleAccountMenu,
            handleAccountConnected: handleAccountConnectedComposable,
            getAccountName,
            getAccountPhoneNumber,
            getAccountIdForGroup,
            rebuildAllGroups,
            stopPollingQRCode,
            stopPollingStatus,
            updateAccountsFromData
        } = useWhatsAppAccounts()

        const messageText = ref('')
        const uploadedFile = ref<File | null>(null)
        const previewImage = ref<string | null>(null)
        const uploadedFileUrl = ref<string | null>(null)
        const isUploadingFile = ref(false)
        const fileInput = ref<HTMLInputElement | null>(null)

        const groups = ref<GroupInfo[]>([])
        const selectedGroups = ref<SelectedGroup[]>([])

        // Configuration Management Composable
        const {
            availableConfigurations,
            selectedConfig,
            isLoadingConfiguration,
            isSavingConfiguration,
            openConfigurations,
            showCreateConfigModal,
            editingConfig,
            editingConfigIndex,
            configGroupsExpanded,
            getConfigGroupsCount,
            getAccessibleGroupsCount,
            getUnaccessibleGroupsCount,
            toggleConfigGroups,
            loadConfigurations,
            addSelectedConfiguration,
            resetSelectedConfiguration,
            editSelectedConfiguration,
            deleteSelectedConfiguration,
            closeConfiguration,
            openCreateConfigModal,
            editConfiguration,
            addGroupToConfig,
            removeGroupFromConfig,
            addTemplateToConfig,
            removeTemplateFromConfig,
            ensureTemplateName: ensureTemplateNameInConfig,
            canSaveConfig,
            saveConfiguration,
            closeCreateConfigModal
        } = useWhatsAppConfigurations(allGroups)

        // Helper function for queue composable
        const getGroupName = (groupJid: string) => {
            const group = allGroups.value.find((g: GroupInfo) => g.jid === groupJid)
            return group?.subject || groupJid
        }

        // Template management methods
        const selectedTemplateIndex = ref<number | null>(null)

        const addNewTemplate = () => {
            if (editingConfig.value) {
                addTemplateToConfig(editingConfig.value as WhatsAppConfiguration)
                // Select the newly added template
                if (editingConfig.value.messageTemplates && editingConfig.value.messageTemplates.length > 0) {
                    selectedTemplateIndex.value = editingConfig.value.messageTemplates.length - 1
                }
            }
        }

        const removeTemplate = (templateIndex: number) => {
            if (editingConfig.value) {
                removeTemplateFromConfig(templateIndex, editingConfig.value as WhatsAppConfiguration)
                // Clear selection if we removed the selected template
                if (selectedTemplateIndex.value === templateIndex) {
                    selectedTemplateIndex.value = null
                } else if (selectedTemplateIndex.value !== null && selectedTemplateIndex.value > templateIndex) {
                    // Adjust index if we removed a template before the selected one
                    selectedTemplateIndex.value = selectedTemplateIndex.value - 1
                }
            }
        }

        const ensureTemplateName = (template: MessageTemplate, templateIndex: number) => {
            if (editingConfig.value) {
                ensureTemplateNameInConfig(template, templateIndex, editingConfig.value as WhatsAppConfiguration)
            }
        }

        // Wrapper functions for template calls with type assertions
        const handleAddGroupToConfig = (group: GroupInfo) => {
            if (editingConfig.value) {
                addGroupToConfig(group, editingConfig.value as WhatsAppConfiguration)
            }
        }

        const handleRemoveGroupFromConfig = (group: GroupInfo) => {
            if (editingConfig.value) {
                removeGroupFromConfig(group, editingConfig.value as WhatsAppConfiguration)
            }
        }

        // Wrapper functions for config operations in template
        const handleEditConfiguration = (config: any) => {
            editConfiguration(config as ConfigWithExtras)
        }

        const handleResetSelectedConfiguration = (config: any) => {
            resetSelectedConfiguration(config as ConfigWithExtras)
        }

        const handleGetConfigGroupsCount = (config: any): number => {
            return getConfigGroupsCount(config as ConfigWithExtras)
        }

        const handleGetAccessibleGroupsCount = (config: any): number => {
            return getAccessibleGroupsCount(config as ConfigWithExtras)
        }

        const handleGetUnaccessibleGroupsCount = (config: any): number => {
            return getUnaccessibleGroupsCount(config as ConfigWithExtras)
        }

        // Watch for modal open/close to reset template selection
        watch(showCreateConfigModal, (isOpen) => {
            if (!isOpen) {
                selectedTemplateIndex.value = null
            } else {
                // Select first template when opening modal if templates exist
                nextTick(() => {
                    if (editingConfig.value?.messageTemplates && editingConfig.value.messageTemplates.length > 0) {
                        selectedTemplateIndex.value = 0
                    }
                })
            }
        })

        // Engine Management Composable
        const {
            engineRunning,
            isStarting,
            isStopping,
            startEngine: startEngineComposable,
            stopEngine: stopEngineComposable,
            toggleEngine: toggleEngineComposable,
            checkEngineStatus: checkEngineStatusComposable,
            startStatusPolling,
            stopStatusPolling,
            updateEngineStatusFromData
        } = useWhatsAppEngine(loadAccounts)

        // Queue Management Composable
        const {
            queueStatus,
            queueProcessingEnabled,
            isStartingQueue,
            isStoppingQueue,
            removingFromQueue,
            queueItemsList,
            refreshQueueStatus,
            startQueue,
            stopQueue,
            clearQueue,
            removeFromQueue,
            updateQueueStatusFromData
        } = useWhatsAppQueue(engineRunning, getAccountName, getAccountPhoneNumber, getGroupName, openConfigurations)

        const isSending = ref(false)
        const isLoadingGroups = ref(false)
        
        // Status update countdown
        const statusUpdateCountdown = ref(0)
        const isStatusUpdating = ref(false)
        let statusUpdateTimer: ReturnType<typeof setInterval> | null = null


        // Messages Management Composable
        const {
            allMessages,
            scheduledItemsList,
            sentItemsList,
            deliveredItemsList,
            failedItemsList,
            refreshAllMessages,
            removeMessage,
            postScheduledMessageNow,
            reAddToQueue,
            checkMessageStatus,
            getCountdown,
            startMessagesPolling,
            stopMessagesPolling,
            updateMessagesFromData
        } = useWhatsAppMessages(
            getAccountName,
            getAccountPhoneNumber,
            getGroupName,
            openConfigurations,
            engineRunning,
            convertUTCToEasternDateTimeLocal,
            refreshQueueStatus
        )

        const isLoadingAccountGroups = ref<Record<string, boolean>>({})

        const configMediaFileInputs = ref<Record<number, HTMLInputElement | null>>({})

        // Media Management Composable
        const {
            globalMediaUrl,
            globalMediaIsImage,
            isUploadingGlobalMedia,
            updateAllMedia,
            handleGlobalMediaUpload,
            clearGlobalMedia,
            handleConfigMediaUpload,
            clearConfigMedia
        } = useWhatsAppMedia(openConfigurations as Ref<ConfigWithExtras[]>)

        // Message Generation & Content Selection Composable
        const {
            selectedContentId,
            selectedContent,
            selectedDealId, // Backward compatibility
            selectedDeal, // Backward compatibility
            globalTagline,
            onContentSelected,
            onDealSelected, // Backward compatibility
            generateMessagesForAllConfigurations,
            generateMessagesForConfiguration,
            updateConfigTagline,
            updateAllTaglines,
            updateConfigMessage,
            applyAllChanges,
            restoreDefaultMessage,
            formatWhatsAppMessage,
            getMessageUrl
        } = useWhatsAppMessageGeneration(openConfigurations as Ref<ConfigWithExtras[]>, globalMediaUrl, updateAllMedia)

        // Watch for content changes and clear image uploads before new content is selected
        watch(selectedContent, (newContent, oldContent) => {
            // Only clear if content is actually changing (not just initializing)
            if (oldContent !== undefined && newContent !== oldContent) {
                // Clear media uploads for all configurations and set content image if template requires it
                openConfigurations.value.forEach(config => {
                    // Check if the selected template has useContentImage enabled
                    const selectedTemplate = config.messageTemplates?.find((t: any) => t.name === config.selectedTemplateName)
                    if (selectedTemplate?.useContentImage && newContent) {
                        // Set content image if template requires it
                        const contentImageUrl = getContentImageUrl(newContent)
                        if (contentImageUrl) {
                            config.mediaUrl = contentImageUrl
                            config.mediaIsImage = true
                        } else {
                            // Clear if no content image available
                            config.mediaUrl = null
                            config.mediaIsImage = false
                        }
                    } else {
                        // Clear media uploads if template doesn't use content image
                        config.mediaUrl = null
                        config.mediaIsImage = false
                    }
                })
            }
        })

        // Helper functions for content properties
        const getContentTitle = (content: any): string => {
            if (!content) return ''
            if (content instanceof DealDetails || ('dealId' in content && typeof content.dealId === 'number')) {
                return (content as DealDetails).title || ''
            }
            if (content instanceof Article || ('articleId' in content && typeof content.articleId === 'number')) {
                return (content as Article).title || ''
            }
            if (content instanceof OutletItem || ('id' in content && typeof content.id === 'string' && 'name' in content)) {
                return (content as OutletItem).name || ''
            }
            return ''
        }

        const getContentDescription = (content: any): string => {
            if (!content) return ''
            if (content instanceof DealDetails || ('dealId' in content && typeof content.dealId === 'number')) {
                return (content as DealDetails).description || ''
            }
            if (content instanceof Article || ('articleId' in content && typeof content.articleId === 'number')) {
                return (content as Article).desc || ''
            }
            if (content instanceof OutletItem || ('id' in content && typeof content.id === 'string' && 'name' in content)) {
                return (content as OutletItem).description || (content as OutletItem).subheading || ''
            }
            return ''
        }

        const getContentImageUrl = (content: any): string => {
            if (!content) return ''
            if (content instanceof DealDetails || ('dealId' in content && typeof content.dealId === 'number')) {
                return (content as DealDetails).imgUrl || ''
            }
            if (content instanceof Article || ('articleId' in content && typeof content.articleId === 'number')) {
                return (content as Article).imgUrl || ''
            }
            if (content instanceof OutletItem || ('id' in content && typeof content.id === 'string' && 'name' in content)) {
                return (content as OutletItem).imgSrc || (content as OutletItem).bannerImgSrc || ''
            }
            return ''
        }

        const onTemplateChange = (index: number, templateName: string) => {
            const config = openConfigurations.value[index]
            if (!config) return

            config.selectedTemplateName = templateName

            // Find the selected template
            const selectedTemplate = config.messageTemplates?.find(t => t.name === templateName)
            const templateContent = selectedTemplate?.content || config.defaultMessage || ''

            // Update edited message with template content
            config.editedMessage = templateContent

            // If template has useContentImage enabled and content is selected, set mediaUrl to content image
            if (selectedTemplate?.useContentImage && selectedContent.value) {
                const contentImageUrl = getContentImageUrl(selectedContent.value)
                if (contentImageUrl) {
                    config.mediaUrl = contentImageUrl
                    config.mediaIsImage = true
                }
            }

            // Regenerate message if content is selected
            if (selectedContent.value) {
                generateMessagesForConfiguration(index)
            } else {
                config.displayMessage = templateContent
            }
        }

        const globalMediaFileInput = ref<HTMLInputElement | null>(null)
        const newConfigSelectedGroup = ref<GroupInfo | null>(null)


        // Show Groups State
        const showGroups = ref(false)

        // Add Account Modal State
        const showAddAccountModal = ref(false)
        const newAccountId = ref('')
        const qrCode = ref<string | null>(null)
        const accountStatus = ref<'disconnected' | 'connecting' | 'connected'>('disconnected')
        const isCreatingAccount = ref(false)

        const currentTime = computed(() => {
            return new Date().toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit'
            })
        })

        const isImageFile = computed(() => {
            return uploadedFile.value?.type.startsWith('image/') || false
        })

        const canSend = computed(() => {
            return (messageText.value.trim() || uploadedFile.value) && selectedGroups.value.length > 0
        })


        const formatFileSize = (bytes: number): string => {
            if (bytes === 0) return '0 Bytes'
            const k = 1024
            const sizes = ['Bytes', 'KB', 'MB', 'GB']
            const i = Math.floor(Math.log(bytes) / Math.log(k))
            return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
        }


        const hasGroupAccess = (groupJid: string): boolean => {
            return allGroups.value.some(g => g.jid === groupJid);
        }


        const qrCodeImageUrl = computed(() => {
            if (!qrCode.value) {
                console.log('qrCodeImageUrl: No QR code value')
                return ''
            }
            console.log('qrCodeImageUrl: QR code value length:', qrCode.value.length)
            console.log('qrCodeImageUrl: QR code value preview:', qrCode.value.substring(0, 100))

            // Check if it's already a base64 image
            if (qrCode.value.startsWith('data:image')) {
                console.log('qrCodeImageUrl: Using as-is (data URL)')
                return qrCode.value
            }
            // If it's a base64 string without data URL prefix, add it
            if (qrCode.value.startsWith('/9j/') || qrCode.value.startsWith('iVBOR')) {
                console.log('qrCodeImageUrl: Adding data URL prefix')
                return `data:image/png;base64,${qrCode.value}`
            }
            // Otherwise, treat it as QR code data and use QR code API
            const url = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrCode.value)}`
            console.log('qrCodeImageUrl: Generated QR API URL:', url.substring(0, 150))
            return url
        })

        const generateAccountId = (): string => {
            return `account-${Date.now()}`
        }

        const initiateAddAccount = async () => {
            newAccountId.value = generateAccountId()
            showAddAccountModal.value = true

            isCreatingAccount.value = true
            try {
                await createAccountComposable(newAccountId.value, qrCode, accountStatus)
            } catch (error) {
                closeAddAccountModal()
            } finally {
                isCreatingAccount.value = false
            }
        }

        const handleQRImageError = (event: Event) => {
            console.error('QR code image failed to load:', event)
            const img = event.target as HTMLImageElement
            console.error('Image src:', img.src)
        }

        const handleQRImageLoad = () => {
            console.log('QR code image loaded successfully')
        }

        const handleAccountConnected = async () => {
            await handleAccountConnectedComposable(newAccountId.value)
            closeAddAccountModal()
        }

        const closeAddAccountModal = () => {
            showAddAccountModal.value = false
            newAccountId.value = ''
            qrCode.value = null
            accountStatus.value = 'disconnected'
            stopPollingQRCode()
            stopPollingStatus()
        }

        const checkGroupAccessForConfig = (groupJids: string[]): { [groupJid: string]: string[] } => {
            const accessMap: { [groupJid: string]: string[] } = {}

            groupJids.forEach(jid => {
                accessMap[jid] = []
            })

            for (const account of accounts.value) {
                if (account.status === 'connected' && account.groups.length > 0) {
                    account.groups.forEach(group => {
                        for (const requestedJid of groupJids) {
                            if (group.jid === requestedJid) {
                                if (!accessMap[requestedJid].includes(account.id)) {
                                    accessMap[requestedJid].push(account.id)
                                }
                            }
                        }
                    })
                }
            }

            return accessMap
        }

        // Close account menu when clicking outside
        const handleClickOutside = (event: MouseEvent) => {
            if (openAccountMenu.value) {
                const menuContainer = (event.target as HTMLElement).closest('.account-menu-container')
                if (!menuContainer) {
                    openAccountMenu.value = null
                }
            }
        }




        // Deal Selection Functions

        const setConfigMediaFileInput = (el: any, index: number) => {
            if (el) {
                configMediaFileInputs.value[index] = el as HTMLInputElement
            } else {
                // Clean up when element is removed
                delete configMediaFileInputs.value[index]
            }
        }

        const triggerConfigMediaFileInput = (index: number) => {
            const input = configMediaFileInputs.value[index]
            if (input) {
                input.click()
            }
        }

        const triggerGlobalMediaFileInput = () => {
            if (globalMediaFileInput.value) {
                globalMediaFileInput.value.click()
            }
        }




        const sendConfigurationToQueue = async (index: number) => {
            const config = openConfigurations.value[index]
            const messageToSend = config.displayMessage || config.generatedMessage

            // Use groups if available, otherwise fall back to accountGroups for backward compatibility
            const groupsToSend = config.groups;

            if (!messageToSend || groupsToSend.length === 0) {
                util.showToast('Configuration has no message or groups', { icon: 'error', iconClass: 'toast-icon-circle' })
                return
            }

            // Set loading state
            config.isSendingToQueue = true

            try {
                // Determine if media is image or document
                let imageUrl: string | null = null
                let documentUrl: string | null = null

                // Check if selected template has useContentImage enabled
                const selectedTemplate = config.messageTemplates?.find(t => t.name === config.selectedTemplateName)
                const shouldUseContentImage = selectedTemplate?.useContentImage === true

                if (shouldUseContentImage && selectedContent.value) {
                    // Use content image if template has useContentImage enabled and content has an image
                    const contentImageUrl = getContentImageUrl(selectedContent.value)
                    if (contentImageUrl) {
                        imageUrl = contentImageUrl
                        documentUrl = null // Explicitly set to null when using content image
                    } else if (config.mediaUrl) {
                        // Fallback to uploaded media if content has no image
                        if (config.mediaIsImage) {
                            imageUrl = config.mediaUrl
                            documentUrl = null
                        } else {
                            documentUrl = config.mediaUrl
                            imageUrl = null
                        }
                    }
                } else if (config.mediaUrl) {
                    // Use uploaded media if useContentImage is not enabled
                    if (config.mediaIsImage) {
                        imageUrl = config.mediaUrl
                        documentUrl = null
                    } else {
                        documentUrl = config.mediaUrl
                        imageUrl = null
                    }
                }

                // Handle scheduling and delay
                let scheduledAt: string | null = null
                let messageType: MessageType | undefined = undefined

                if (config.sendMode === 'schedule' && config.scheduledDateTime) {
                    // Convert datetime-local (interpreted as Eastern time) to UTC ISO string
                    scheduledAt = convertEasternToUTC(config.scheduledDateTime)
                    messageType = MessageType.Scheduled
                } else if (config.sendMode === 'delay') {
                    // For delay mode, we need to find last message per group
                    // Refresh queue status to get current state
                    if (engineRunning.value) {
                        await refreshQueueStatus()
                    }
                }

                // Replace {{uniqueId}} placeholder for each group with uniqueIdPrefix + (index + 1)
                const uniqueIdPrefix = config.uniqueIdPrefix || ''
                const groupMessages = groupsToSend.map((g, groupIndex) => {
                    if (messageToSend.includes('{{uniqueId}}')) {
                        const uniqueId = uniqueIdPrefix + (groupIndex + 1).toString()
                        return messageToSend.replace(/\{\{uniqueId\}\}/g, uniqueId)
                    }
                    return messageToSend
                })

                // Helper function to find last pending or delayed message for a group
                const findLastPendingOrDelayedMessage = (groupJid: string): { scheduledAt?: string, isPending?: boolean } | null => {
                    let latestDelayed: WhatsAppMessageDto | null = null
                    let hasPending = false

                    // Check for delayed messages in all messages (exclude explicitly scheduled)
                    Object.entries(allMessages.value).forEach(([accountId, items]: [string, any]) => {
                        if (Array.isArray(items)) {
                            items.forEach((item: WhatsAppMessageDto) => {
                                if (item.groupJid === groupJid && 
                                    item.status === 'scheduled' && 
                                    item.messageType === MessageType.Delayed) {
                                    if (!latestDelayed || new Date(item.scheduledAt).getTime() > new Date(latestDelayed.scheduledAt).getTime()) {
                                        latestDelayed = item
                                    }
                                }
                            })
                        }
                    })

                    // Check for pending messages in queue status
                    Object.entries(queueStatus.value).forEach(([accountId, items]) => {
                        items.forEach(item => {
                            if (item.groupJid === groupJid && item.status === 'pending') {
                                hasPending = true
                            }
                        })
                    })

                    // If we have a delayed message, use its scheduledAt
                    if (latestDelayed) {
                        return { scheduledAt: latestDelayed.scheduledAt }
                    }

                    // If we have pending messages but no delayed, use current time (pending messages will be sent "soon")
                    if (hasPending) {
                        return { isPending: true }
                    }

                    return null
                }

                // Send groups without accountId - backend will find appropriate account
                // Each group gets its own message with uniqueId replaced
                await Promise.all(groupsToSend.map(async (g, groupIndex) => {
                    // Find account that has access to this group to get phone number
                    const accountIdForGroup = getAccountIdForGroup(g.jid)
                    const phoneNumber = accountIdForGroup ? getAccountPhoneNumber(accountIdForGroup) : undefined

                    let groupScheduledAt: string | null = null
                    let groupMessageType: MessageType | undefined = undefined
                    let groupDelayHours: number | undefined = undefined
                    let groupDelayMinutes: number | undefined = undefined
                    let groupDelaySeconds: number | undefined = undefined

                    if (config.sendMode === 'delay') {
                        const lastMessage = findLastPendingOrDelayedMessage(g.jid)
                        let baseTime: Date
                        
                        if (lastMessage) {
                            // Calculate scheduledAt based on last message time + delay
                            if (lastMessage.scheduledAt) {
                                // Use scheduledAt from delayed message
                                baseTime = new Date(lastMessage.scheduledAt)
                            } else if (lastMessage.isPending) {
                                // Use current time for pending messages (they'll be sent "soon")
                                baseTime = new Date()
                            } else {
                                baseTime = new Date()
                            }
                        } else {
                            // No last message found, use current time + delay
                            baseTime = new Date()
                        }
                        
                        const delayMs = (config.delayHours || 0) * 60 * 60 * 1000 + (config.delayMinutes || 0) * 60 * 1000 + (config.delaySeconds || 0) * 1000
                        const calculatedTime = new Date(baseTime.getTime() + delayMs)
                        groupScheduledAt = calculatedTime.toISOString()
                        groupMessageType = MessageType.Delayed
                        
                        // Still send delayHours, delayMinutes, and delaySeconds for backend calculation as fallback
                        groupDelayHours = config.delayHours
                        groupDelayMinutes = config.delayMinutes
                        groupDelaySeconds = config.delaySeconds
                    } else {
                        groupScheduledAt = scheduledAt
                        groupMessageType = messageType
                    }

                    await api.whatsapp.addToQueue({
                        groups: [{
                            accountId: '', // Empty - backend will find account dynamically
                            groupJid: g.jid,
                            id: crypto.randomUUID(),
                            phoneNumber: phoneNumber
                        }],
                        message: groupMessages[groupIndex],
                        imageUrl: imageUrl && imageUrl.trim() ? imageUrl : null,
                        documentUrl: documentUrl && documentUrl.trim() ? documentUrl : null,
                        configurationName: config.name,
                        scheduledAt: groupScheduledAt,
                        skipPreview: config.skipPreview || false,
                        messageType: groupMessageType,
                        delayHours: groupDelayHours,
                        delayMinutes: groupDelayMinutes,
                        delaySeconds: groupDelaySeconds
                    })
                }))

                if (config.sendMode === 'schedule' || config.sendMode === 'delay') {
                    util.showToast(`Message ${config.sendMode === 'delay' ? 'scheduled with delay' : 'scheduled'} for ${groupsToSend.length} group(s)`, { icon: 'check_circle', iconClass: 'toast-icon-circle' })
                } else {
                    if (engineRunning.value) {
                        await refreshQueueStatus()
                    }
                    util.showToast(`Message sent to queue for ${groupsToSend.length} group(s)`, { icon: 'check_circle', iconClass: 'toast-icon-circle' })
                }
            } catch (error: any) {
                console.error('Failed to send configuration to queue:', error)
                const errorMsg = error.response?.data?.error || error.response?.data?.warning || error.message || 'Failed to send to queue'
                util.showToast(`Failed to send to queue: ${errorMsg}`, { icon: 'error', iconClass: 'toast-icon-circle' })
            } finally {
                config.isSendingToQueue = false
            }
        }

        const saveScheduledTime = async (accountId: string, id: string) => {
            const scheduledItems = scheduledItemsList.value
            const message = scheduledItems.find((m: any) => m.accountId === accountId && m.id === id)
            if (!message || message.status !== 'scheduled') return

            const editedValue = message.scheduledAtLocal
            if (!editedValue) return

            console.log('editedValue:', editedValue)

            try {
                const scheduledAtUTC = convertEasternToUTC(editedValue)
                await api.whatsapp.updateScheduledMessageTime(accountId, id, scheduledAtUTC)
                util.showToast('Scheduled time updated', { icon: 'check_circle', iconClass: 'toast-icon-circle' })
            } catch (error: any) {
                console.error('Failed to update scheduled time:', error)
                const errorMsg = error.response?.data?.error || 'Failed to update scheduled time'
                util.showToast(errorMsg, { icon: 'error', iconClass: 'toast-icon-circle' })
            }
        }

        const getCountdownForItem = (scheduledAt: string): string => {
            return getCountdown(scheduledAt)
        }


        const handleFileUpload = async (event: Event) => {
            const target = event.target as HTMLInputElement
            const file = target.files?.[0]
            if (file) {
                await processFile(file)
            }
        }

        const handleDrop = async (event: DragEvent) => {
            const file = event.dataTransfer?.files?.[0]
            if (file) {
                await processFile(file)
                // Also update the file input if it exists
                if (fileInput.value) {
                    const dataTransfer = new DataTransfer()
                    dataTransfer.items.add(file)
                    fileInput.value.files = dataTransfer.files
                }
            }
        }

        const processFile = async (file: File) => {
            uploadedFile.value = file
            uploadedFileUrl.value = null

            // Create preview for images
            if (file.type.startsWith('image/')) {
                const reader = new FileReader()
                reader.onload = (e) => {
                    previewImage.value = e.target?.result as string
                }
                reader.readAsDataURL(file)
            } else {
                previewImage.value = null
            }

            // Upload file immediately
            await uploadFile(file)
        }

        const uploadFile = async (file: File) => {
            isUploadingFile.value = true
            try {
                const formData = new FormData()
                formData.append('file', file)
                const uploadResponse = await api.whatsapp.uploadFile(formData)
                uploadedFileUrl.value = uploadResponse.data.url
            } catch (error) {
                console.error('Failed to upload file:', error)
                util.showToast('Failed to upload file. Please try again.', { icon: 'error', iconClass: 'toast-icon-circle' })
            } finally {
                isUploadingFile.value = false
            }
        }

        const clearFile = () => {
            uploadedFile.value = null
            previewImage.value = null
            uploadedFileUrl.value = null
            if (fileInput.value) {
                fileInput.value.value = ''
            }
        }

        const sendMessage = async () => {
            if (!canSend.value) return

            // Ensure file is uploaded
            if (uploadedFile.value && !uploadedFileUrl.value && !isUploadingFile.value) {
                await uploadFile(uploadedFile.value)
            }

            // Wait for upload to complete if in progress
            if (isUploadingFile.value) {
                // Wait a bit for upload to complete
                await new Promise(resolve => setTimeout(resolve, 500))
            }

            if (uploadedFile.value && !uploadedFileUrl.value) {
                util.showToast('Please wait for file upload to complete.', { icon: 'info' })
                return
            }

            isSending.value = true
            try {
                // Use already uploaded file URL
                let imageUrl: string | undefined
                let documentUrl: string | undefined

                if (uploadedFileUrl.value) {
                    if (uploadedFile.value?.type.startsWith('image/')) {
                        imageUrl = uploadedFileUrl.value
                    } else {
                        documentUrl = uploadedFileUrl.value
                    }
                }

                // Add to queue for each selected group
                await api.whatsapp.addToQueue({
                    groups: selectedGroups.value.map(g => ({
                        accountId: g.accountId,
                        groupJid: g.groupJid,
                        id: crypto.randomUUID(),
                        phoneNumber: getAccountPhoneNumber(g.accountId)
                    })),
                    message: messageText.value,
                    configurationName: null,
                    imageUrl,
                    documentUrl,
                    scheduledAt: null,
                    skipPreview: false
                })

                // Clear form
                messageText.value = ''
                clearFile()

                // Refresh queue status only if engine is running
                if (engineRunning.value) {
                    await refreshQueueStatus()
                }
            } catch (error) {
                console.error('Failed to send message:', error)
                util.showToast('Failed to send message. Please try again.', { icon: 'error', iconClass: 'toast-icon-circle' })
            } finally {
                isSending.value = false
            }
        }

        const startEngine = async () => {
            const success = await startEngineComposable()
            if (success) {
                startStatusUpdateCountdown()
                await refreshQueueStatus()
                startStatusPolling(
                    async () => {
                        const status = await checkEngineStatusComposable()
                        if (status) {
                            queueProcessingEnabled.value = status.queueProcessingEnabled
                            if (!status.wasRunning && status.running) {
                                await refreshQueueStatus()
                            }
                        }
                        await refreshQueueStatus()
                    },
                    () => !engineRunning.value
                )
            }
        }

        const stopEngine = async () => {
            const success = await stopEngineComposable()
            if (success) {
                stopStatusUpdateCountdown()
                queueProcessingEnabled.value = false
                queueStatus.value = {}
                stopStatusPolling()
            }
        }

        const toggleEngine = async () => {
            await toggleEngineComposable()
            if (engineRunning.value) {
                startStatusUpdateCountdown()
                await refreshQueueStatus()
                startStatusPolling(
                    async () => {
                        const status = await checkEngineStatusComposable()
                        if (status) {
                            queueProcessingEnabled.value = status.queueProcessingEnabled
                            if (!status.wasRunning && status.running) {
                                await refreshQueueStatus()
                            }
                        }
                        await refreshQueueStatus()
                    },
                    () => !engineRunning.value
                )
            } else {
                stopStatusUpdateCountdown()
                queueProcessingEnabled.value = false
                queueStatus.value = {}
                stopStatusPolling()
            }
        }

        const checkEngineStatus = async () => {
            const status = await checkEngineStatusComposable()
            if (status) {
                queueProcessingEnabled.value = status.queueProcessingEnabled
                if (!status.wasRunning && status.running) {
                    await refreshQueueStatus()
                }
                if (status.wasRunning && !status.running) {
                    queueStatus.value = {}
                }
            }
        }

        const startStatusUpdateCountdown = () => {
            // Clear any existing timer
            if (statusUpdateTimer) {
                clearInterval(statusUpdateTimer)
            }
            
            // Reset countdown
            statusUpdateCountdown.value = 5
            isStatusUpdating.value = true
            
            // Start countdown - continuously loop
            statusUpdateTimer = setInterval(() => {
                statusUpdateCountdown.value--
                if (statusUpdateCountdown.value <= 0) {
                    // Reset to 5 and continue
                    statusUpdateCountdown.value = 5
                }
            }, 1000)
        }

        const stopStatusUpdateCountdown = () => {
            if (statusUpdateTimer) {
                clearInterval(statusUpdateTimer)
                statusUpdateTimer = null
            }
            isStatusUpdating.value = false
            statusUpdateCountdown.value = 0
        }

        const clearMessagesByStatus = async (status: string, statusLabel: string) => {
            if (!confirm(`Are you sure you want to clear all ${statusLabel.toLowerCase()} messages? This action cannot be undone.`)) {
                return
            }
            try {
                const response = await api.whatsapp.clearMessages(status)
                util.showToast(`${statusLabel} messages cleared successfully. ${response.data.count || 0} message(s) removed.`, { icon: 'success', iconClass: 'toast-icon-circle' })
                await refreshAllMessages()
            } catch (error) {
                console.error(`Failed to clear ${statusLabel.toLowerCase()} messages:`, error)
                util.showToast(`Failed to clear ${statusLabel.toLowerCase()} messages. Please try again.`, { icon: 'error', iconClass: 'toast-icon-circle' })
            }
        }

        const clearSentMessages = async () => {
            await clearMessagesByStatus('sent', 'Sent')
        }

        const clearScheduledMessages = async () => {
            await clearMessagesByStatus('scheduled', 'Scheduled')
        }

        const clearDeliveredMessages = async () => {
            await clearMessagesByStatus('delivered', 'Delivered')
        }

        const clearFailedMessages = async () => {
            await clearMessagesByStatus('failed', 'Failed')
        }

        const handleClearTab = async (tabId: string) => {
            if (tabId === 'queue-status') {
                await clearQueue()
            } else if (tabId === 'scheduled-messages') {
                await clearScheduledMessages()
            } else if (tabId === 'sent-messages') {
                await clearSentMessages()
            } else if (tabId === 'delivered-messages') {
                await clearDeliveredMessages()
            } else if (tabId === 'failed-messages') {
                await clearFailedMessages()
            }
        }

        const getClearButtonTitle = (tabId: string): string => {
            if (tabId === 'queue-status') return 'Clear Queue'
            if (tabId === 'scheduled-messages') return 'Clear Scheduled Messages'
            if (tabId === 'sent-messages') return 'Clear Sent Messages'
            if (tabId === 'delivered-messages') return 'Clear Delivered Messages'
            if (tabId === 'failed-messages') return 'Clear Failed Messages'
            return 'Clear'
        }

        const formatTime = (timeString: string | null): string => {
            if (!timeString) return ''
            try {
                const date = new Date(timeString)
                // Format in Eastern time
                return date.toLocaleTimeString('en-US', {
                    timeZone: 'America/New_York',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: true
                })
            } catch {
                return timeString
            }
        }



        const queueItemsCount = computed(() => queueItemsList.value.length)
        const scheduledItemsCount = computed(() => scheduledItemsList.value.length)
        const sentItemsCount = computed(() => sentItemsList.value.length)
        const deliveredItemsCount = computed(() => deliveredItemsList.value.length)
        const failedItemsCount = computed(() => failedItemsList.value.length)

        // Pagination state
        const itemsPerPage = 15
        const queuePage = ref(1)
        const scheduledPage = ref(1)
        const sentPage = ref(1)
        const deliveredPage = ref(1)
        const failedPage = ref(1)

        // Paginated lists
        const paginatedQueueItems = computed(() => {
            const start = (queuePage.value - 1) * itemsPerPage
            const end = start + itemsPerPage
            return queueItemsList.value.slice(start, end)
        })

        const paginatedScheduledItems = computed(() => {
            const start = (scheduledPage.value - 1) * itemsPerPage
            const end = start + itemsPerPage
            return scheduledItemsList.value.slice(start, end)
        })

        const paginatedSentItems = computed(() => {
            const start = (sentPage.value - 1) * itemsPerPage
            const end = start + itemsPerPage
            return sentItemsList.value.slice(start, end)
        })

        const paginatedDeliveredItems = computed(() => {
            const start = (deliveredPage.value - 1) * itemsPerPage
            const end = start + itemsPerPage
            return deliveredItemsList.value.slice(start, end)
        })

        const paginatedFailedItems = computed(() => {
            const start = (failedPage.value - 1) * itemsPerPage
            const end = start + itemsPerPage
            return failedItemsList.value.slice(start, end)
        })

        // Total pages
        const queueTotalPages = computed(() => Math.ceil(queueItemsList.value.length / itemsPerPage))
        const scheduledTotalPages = computed(() => Math.ceil(scheduledItemsList.value.length / itemsPerPage))
        const sentTotalPages = computed(() => Math.ceil(sentItemsList.value.length / itemsPerPage))
        const deliveredTotalPages = computed(() => Math.ceil(deliveredItemsList.value.length / itemsPerPage))
        const failedTotalPages = computed(() => Math.ceil(failedItemsList.value.length / itemsPerPage))

        // Pagination functions
        const goToQueuePage = (page: number) => {
            if (page >= 1 && page <= queueTotalPages.value) {
                queuePage.value = page
            }
        }

        const goToScheduledPage = (page: number) => {
            if (page >= 1 && page <= scheduledTotalPages.value) {
                scheduledPage.value = page
            }
        }

        const goToSentPage = (page: number) => {
            if (page >= 1 && page <= sentTotalPages.value) {
                sentPage.value = page
            }
        }

        const goToDeliveredPage = (page: number) => {
            if (page >= 1 && page <= deliveredTotalPages.value) {
                deliveredPage.value = page
            }
        }

        const goToFailedPage = (page: number) => {
            if (page >= 1 && page <= failedTotalPages.value) {
                failedPage.value = page
            }
        }

        const handleTabChange = (tabId: string) => {
            // Reset pagination to page 1 when switching tabs
            if (tabId === 'queue-status') {
                queuePage.value = 1
            } else if (tabId === 'scheduled-messages') {
                scheduledPage.value = 1
            } else if (tabId === 'sent-messages') {
                sentPage.value = 1
            } else if (tabId === 'delivered-messages') {
                deliveredPage.value = 1
            } else if (tabId === 'failed-messages') {
                failedPage.value = 1
            }
        }

        const tabs = computed(() => [
            { id: 'queue-status', label: 'Queue Status', count: queueItemsList.value.length },
            { id: 'scheduled-messages', label: 'Scheduled Messages', count: scheduledItemsList.value.length },
            { id: 'sent-messages', label: 'Sent Messages', count: sentItemsList.value.length },
            { id: 'delivered-messages', label: 'Delivered Messages', count: deliveredItemsList.value.length },
            { id: 'failed-messages', label: 'Failed Messages', count: failedItemsList.value.length }
        ])

        // Consolidated function to refresh all status from getAllStatus endpoint
        const refreshAllStatus = async () => {
            try {
                const response = await api.whatsapp.getAllStatus()
                const data = response.data

                // Update engine status
                if (data.engineStatus) {
                    updateEngineStatusFromData(
                        data.engineStatus.running || false,
                        data.engineStatus.queueProcessingEnabled || false
                    )
                }

                // Update queue status
                if (data.queueStatus != null && typeof data.queueStatus === 'object') {
                    updateQueueStatusFromData(
                        data.queueStatus,
                        data.queueProcessingStatus?.enabled
                    )
                }

                // Update queue processing status (if not already set from engineStatus)
                if (data.queueProcessingStatus) {
                    updateQueueStatusFromData(
                        data.queueStatus,
                        data.queueProcessingStatus.enabled || false
                    )
                }

                // Update messages
                if (data.messages != null && typeof data.messages === 'object') {
                    updateMessagesFromData(data.messages)
                }
            } catch (error) {
                console.error('Failed to refresh all status:', error)
            }
        }

        onMounted(async () => {
            // Load accounts once on mount (not included in getAllStatus)
            await loadAccounts()
            
            // Load configurations separately (not included in getAllStatus)
            await loadConfigurations()
            
            // Use consolidated endpoint to get all status and messages in one call
            await refreshAllStatus()

            // Always show the status update countdown spinner
            startStatusUpdateCountdown()

            // Add click outside handler for account menus
            document.addEventListener('click', handleClickOutside)

            // Always start polling for status updates (regardless of engine state)
            // This calls getAllStatus which includes: engine status, queue status, and messages
            startStatusPolling(
                async () => {
                    try {
                        // Use consolidated endpoint to get all status at once (includes messages)
                        await refreshAllStatus()
                    } catch (error) {
                        console.error('Error in status polling:', error)
                    }
                }
            )
        })

        onUnmounted(() => {
            stopStatusPolling()
            stopPollingQRCode()
            stopPollingStatus()
            stopStatusUpdateCountdown()
        })

        return {
            messageText,
            uploadedFile,
            previewImage,
            fileInput,
            accounts,
            groups,
            selectedGroups,
            engineRunning,
            queueProcessingEnabled,
            isStarting,
            isStopping,
            isStartingQueue,
            isStoppingQueue,
            isSending,
            isLoadingAccounts,
            isLoadingGroups,
            queueStatus,
            statusUpdateCountdown,
            isStatusUpdating,
            isLoadingAccountGroups,
            refreshAccountGroups,
            currentTime,
            formatTime,
            canSend,
            getAccountName,
            getGroupName,
            hasGroupAccess,
            getConfigGroupsCount,
            getAccessibleGroupsCount,
            getUnaccessibleGroupsCount,
            handleGetConfigGroupsCount,
            handleGetAccessibleGroupsCount,
            handleGetUnaccessibleGroupsCount,
            toggleConfigGroups,
            configGroupsExpanded,
            handleFileUpload,
            handleDrop,
            clearFile,
            sendMessage,
            isImageFile,
            uploadedFileUrl,
            isUploadingFile,
            formatFileSize,
            startEngine,
            stopEngine,
            toggleEngine,
            startQueue,
            stopQueue,
            showAddAccountModal,
            newAccountId,
            qrCode,
            qrCodeImageUrl,
            accountStatus,
            isCreatingAccount,
            initiateAddAccount,
            disconnectAccount,
            handleAccountConnected,
            closeAddAccountModal,
            handleQRImageError,
            handleQRImageLoad,
            checkAccountStatus,
            availableConfigurations,
            selectedConfig,
            isLoadingConfiguration,
            openConfigurations,
            showCreateConfigModal,
            editingConfig,
            editingConfigIndex,
            addGroupToConfig,
            removeGroupFromConfig,
            handleAddGroupToConfig,
            handleRemoveGroupFromConfig,
            addTemplateToConfig,
            removeTemplateFromConfig,
            ensureTemplateNameInConfig,
            addNewTemplate,
            removeTemplate,
            ensureTemplateName,
            selectedTemplateIndex,
            canSaveConfig,
            isSavingConfiguration,
            saveConfiguration,
            closeCreateConfigModal,
            loadConfigurations,
            addSelectedConfiguration,
            editSelectedConfiguration,
            deleteSelectedConfiguration,
            closeConfiguration,
            openCreateConfigModal,
            editConfiguration,
            resetSelectedConfiguration,
            handleEditConfiguration,
            handleResetSelectedConfiguration,
            rebuildAllGroups,
            checkGroupAccessForConfig,
            allGroups,
            restoreDefaultMessage,
            selectedContentId,
            selectedContent,
            selectedDealId,
            selectedDeal,
            globalTagline,
            onContentSelected,
            onDealSelected,
            onTemplateChange,
            getContentTitle,
            getContentDescription,
            getContentImageUrl,
            generateMessagesForAllConfigurations,
            updateConfigTagline,
            updateConfigMessage,
            updateAllTaglines,
            applyAllChanges,
            formatWhatsAppMessage,
            getMessageUrl,
            sendConfigurationToQueue,
            queueItemsList,
            removeFromQueue,
            removingFromQueue,
            openAccountMenu,
            toggleAccountMenu,
            handleClickOutside,
            globalMediaUrl,
            globalMediaIsImage,
            isUploadingGlobalMedia,
            globalMediaFileInput,
            handleGlobalMediaUpload,
            clearGlobalMedia,
            updateAllMedia,
            handleConfigMediaUpload,
            clearConfigMedia,
            setConfigMediaFileInput,
            triggerConfigMediaFileInput,
            triggerGlobalMediaFileInput,
            getMinDateTime,
            formatDateTime,
            convertUTCToEasternDateTimeLocal,
            getCountdown,
            getCountdownForItem,
            saveScheduledTime,
            scheduledItemsList,
            sentItemsList,
            deliveredItemsList,
            failedItemsList,
            paginatedQueueItems,
            paginatedScheduledItems,
            paginatedSentItems,
            paginatedDeliveredItems,
            paginatedFailedItems,
            queuePage,
            scheduledPage,
            sentPage,
            deliveredPage,
            failedPage,
            queueTotalPages,
            scheduledTotalPages,
            sentTotalPages,
            deliveredTotalPages,
            failedTotalPages,
            itemsPerPage,
            goToQueuePage,
            goToScheduledPage,
            goToSentPage,
            goToDeliveredPage,
            goToFailedPage,
            handleTabChange,
            removeMessage,
            postScheduledMessageNow,
            reAddToQueue,
            checkMessageStatus,
            showGroups,
            newConfigSelectedGroup,
            queueItemsCount,
            scheduledItemsCount,
            sentItemsCount,
            deliveredItemsCount,
            failedItemsCount,
            tabs,
            clearSentMessages,
            clearDeliveredMessages,
            clearFailedMessages,
            clearQueue,
            clearScheduledMessages,
            handleClearTab,
            getClearButtonTitle
        }
    }
})
</script>

<style scoped>
.whatsapp-automation {
    padding: 20px;
    max-width: 1400px;
    margin: 0 auto;
    background: #f5f5f5;
    min-height: 100vh;
    text-align: left;
}

.control-label {
    font-weight: 600;
    font-size: 14px;
    color: #333;
    margin-bottom: 4px;
}

.status-indicator {
    font-weight: bold;
    margin-left: 10px;
}

.status-indicator.running {
    color: #4caf50;
}

.status-indicator.stopped {
    color: #f44336;
}

.automation-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
    margin-bottom: 30px;
    align-items: stretch;
    min-height: 600px;
}

.message-section,
.preview-section {
    background: white;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
    height: 100%;
}

.section-header {
    padding: 16px 20px;
    border-bottom: 1px solid #e0e0e0;
    background: #fafafa;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
}

.section-header h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #333;
    flex-shrink: 0;
}

.section-header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    justify-content: flex-end;
}

.queue-control-group {
    display: flex;
    align-items: center;
    gap: 8px;
}

.queue-control-group .control-label {
    font-weight: 600;
    font-size: 13px;
    color: #333;
    margin-right: 4px;
}

.tab-header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: 12px;
}

.header-controls {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
    justify-content: flex-end;
}

.engine-control-group {
    display: flex;
    align-items: center;
    gap: 8px;
}

.header-controls .dropdown-group {
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.header-controls .dropdown-group label {
    font-size: 12px;
    margin-bottom: 0;
    font-weight: 500;
    color: #666;
}

.message-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 20px;
    min-height: 0;
}

.preview-content {
    flex: 1;
    padding: 20px;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.preview-content .whatsapp-preview {
    flex: 1;
    min-height: 0;
}

.message-input {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-family: inherit;
    font-size: 14px;
    resize: none;
    flex: 1;
    min-height: 0;
    box-sizing: border-box;
    transition: border-color 0.2s ease;
    margin-bottom: 15px;
}

.message-input:focus {
    outline: none;
    border-color: #2196F3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.message-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-top: auto;
    padding-top: 15px;
    border-top: 1px solid #f0f0f0;
}

.file-upload-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
}

.file-upload-label {
    display: inline-block;
    cursor: pointer;
    height: 38px;
}

.file-upload-label input[type="file"] {
    display: none;
}

.file-upload-button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 0 16px;
    height: 38px;
    background: #f5f5f5;
    color: #333;
    border: 2px dashed #ddd;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 14px;
    box-sizing: border-box;
    white-space: nowrap;
}

.file-upload-button:hover {
    background: #e8e8e8;
    border-color: #2196F3;
    color: #2196F3;
}

.upload-icon {
    font-size: 16px;
}

.file-preview-small {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    background: #f5f5f5;
    border-radius: 4px;
    border: 1px solid #e0e0e0;
    font-size: 12px;
}

.image-preview-small {
    display: flex;
    align-items: center;
}

.preview-thumbnail-small {
    width: 24px;
    height: 24px;
    object-fit: cover;
    border-radius: 3px;
}

.document-preview-small {
    display: flex;
    align-items: center;
}

.doc-icon-small {
    font-size: 18px;
}

.file-name-small {
    font-size: 12px;
    color: #333;
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.button-remove-small {
    background: #f44336;
    color: white;
    border: none;
    border-radius: 50%;
    width: 18px;
    height: 18px;
    cursor: pointer;
    font-size: 14px;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition: all 0.2s ease;
    flex-shrink: 0;
    margin-left: 4px;
}

.button-remove-small:hover {
    background: #d32f2f;
    transform: scale(1.1);
}

.upload-status-small {
    font-size: 11px;
    color: #666;
    margin-left: 4px;
}

.upload-status-small.success {
    color: #4caf50;
    font-weight: 500;
}


.file-upload-label {
    display: inline-block;
    cursor: pointer;
}

.file-upload-label input[type="file"] {
    display: none;
}

.file-upload-button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: #f5f5f5;
    color: #333;
    border: 2px dashed #ddd;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 14px;
}

.file-upload-button:hover {
    background: #e8e8e8;
    border-color: #2196F3;
    color: #2196F3;
}

.upload-icon {
    font-size: 16px;
}

.file-preview-container {
    position: relative;
    padding: 12px;
    background: #f9f9f9;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    display: flex;
    align-items: center;
    gap: 12px;
}

.image-preview {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
}

.preview-thumbnail {
    width: 60px;
    height: 60px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #ddd;
}

.document-preview {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
}

.document-icon {
    font-size: 32px;
    width: 60px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #e3f2fd;
    border-radius: 4px;
}

.file-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 0;
}

.file-name {
    font-weight: 500;
    font-size: 14px;
    color: #333;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.file-size {
    font-size: 12px;
    color: #666;
}

.button-remove {
    background: #f44336;
    color: white;
    border: none;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    cursor: pointer;
    font-size: 18px;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition: all 0.2s ease;
    flex-shrink: 0;
}

.button-remove:hover {
    background: #d32f2f;
    transform: scale(1.1);
}

.upload-status {
    font-size: 12px;
    color: #666;
    font-style: italic;
}

.upload-status.success {
    color: #4caf50;
    font-weight: 500;
}

.button-small {
    padding: 4px 8px;
    font-size: 12px;
}

.button {
    padding: 0 20px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    height: 38px;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.button-primary {
    background: #2196F3;
    color: white;
}

.button-primary:hover:not(:disabled) {
    background: #1976D2;
    box-shadow: 0 2px 4px rgba(33, 150, 243, 0.3);
}

.button-success {
    background: #4caf50;
    color: white;
}

.button-success:hover:not(:disabled) {
    background: #45a049;
    box-shadow: 0 2px 4px rgba(76, 175, 80, 0.3);
}

.button-danger {
    background: #f44336 !important;
    color: white !important;
}

.button-danger:hover:not(:disabled) {
    background: #da190b;
    box-shadow: 0 2px 4px rgba(244, 67, 54, 0.3);
}

.whatsapp-preview {
    background: #e5ddd5;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    height: 100%;
}

.whatsapp-header {
    background: #075e54;
    color: white;
    padding: 15px;
}

.whatsapp-contact {
    display: flex;
    align-items: center;
    gap: 10px;
}

.contact-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
}

.contact-name {
    font-weight: bold;
}

.contact-status {
    font-size: 12px;
    opacity: 0.8;
}

.whatsapp-messages {
    padding: 20px;
    flex: 1;
    overflow-y: auto;
    min-height: 0;
}

.message-bubble {
    background: #dcf8c6;
    padding: 10px;
    border-radius: 8px;
    max-width: 80%;
    margin-left: auto;
    margin-bottom: 10px;
}

.message-image img {
    max-width: 100%;
    border-radius: 4px;
    margin-bottom: 8px;
}

.message-document {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    background: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
    margin-bottom: 8px;
}

.doc-icon {
    font-size: 20px;
}

.doc-name {
    font-size: 13px;
    color: #333;
    font-weight: 500;
}

.message-text {
    margin-bottom: 5px;
    white-space: pre-wrap;
}

.message-placeholder {
    color: #999;
    font-style: italic;
}

.message-time {
    font-size: 11px;
    color: #666;
    text-align: right;
}

.account-section,
.queue-section {
    background: white;
    border-radius: 8px;
    margin-bottom: 20px;
    border: 1px solid #e0e0e0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
}

.account-section .section-header,
.queue-section .section-header {
    border-bottom: 1px solid #e0e0e0;
}

.account-section-content,
.queue-section-content {
    padding: 20px;
}

.selection-controls {
    display: flex;
    gap: 15px;
    align-items: flex-end;
    margin-bottom: 20px;
}

.selection-controls> :last-child {
    margin-left: auto;
}

.dropdown-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.dropdown-group label {
    font-weight: 500;
    font-size: 14px;
}

.dropdown-group select {
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    height: 38px;
    box-sizing: border-box;
    background: white;
    cursor: pointer;
}

.dropdown-group select:hover {
    border-color: #bbb;
}

.dropdown-group select:focus {
    outline: none;
    border-color: #2196F3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.button-small {
    padding: 0 16px;
    font-size: 13px;
    white-space: nowrap;
    height: 38px;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.groups-grid {
    margin-top: 20px;
}

.groups-grid table {
    width: 100%;
    border-collapse: collapse;
}

.groups-grid th,
.groups-grid td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #ddd;
}

.groups-grid th {
    background: #e0e0e0;
    font-weight: 600;
}

.accounts-groups-grid {
    margin-top: 0;
}

.accounts-groups-table {
    width: 100%;
    border-collapse: collapse;
}

.accounts-groups-table th,
.accounts-groups-table td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #ddd;
    vertical-align: top;
}

.accounts-groups-table th {
    background: #e0e0e0;
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 1;
}

.accounts-groups-table tbody tr:hover {
    background: #f5f5f5;
}

.groups-jids-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 200px;
    overflow-y: auto;
}

.group-jid-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 8px;
    background: #f9f9f9;
    border-radius: 4px;
    font-size: 12px;
}

.group-jid-text {
    font-family: monospace;
    color: #333;
}

.group-subject-text {
    color: #666;
    font-style: italic;
}

.no-groups-text {
    color: #999;
    font-style: italic;
}

.account-actions-cell {
    display: flex;
    align-items: center;
    gap: 8px;
}

.no-groups,
.no-queue,
.no-accounts {
    text-align: center;
    color: #999;
    padding: 40px;
}

.pagination-controls-top {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    padding: 4px 0;
}

.pagination-info-small {
    font-size: 11px;
    color: #666;
    font-weight: 500;
    padding: 0 4px;
}

.pagination-controls-top .button-xs:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.queue-list {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.queue-item {
    background: white;
    padding: 15px;
    border-radius: 4px;
    border: 1px solid #ddd;
}

.queue-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
}

.queue-count {
    color: #666;
    font-size: 14px;
}

.queue-items {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.queue-item-preview {
    font-size: 12px;
    color: #666;
    padding: 5px;
    background: #f9f9f9;
    border-radius: 3px;
}

.queue-more {
    font-size: 12px;
    color: #999;
    font-style: italic;
    margin-top: 5px;
}

.sent-item-preview {
    font-size: 12px;
    padding: 8px;
    background: #f9f9f9;
    border-radius: 3px;
    border-left: 3px solid #ddd;
    margin-bottom: 5px;
}

.sent-item-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
}

.sent-item-status {
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
}

.sent-item-time {
    font-size: 10px;
    color: #999;
}

.sent-item-message {
    color: #666;
    margin-bottom: 4px;
}

.sent-item-error {
    font-size: 10px;
    color: #f44336;
    font-style: italic;
    margin-top: 4px;
}

.status-sent {
    border-left-color: #4caf50;
}

.status-sent .sent-item-status {
    color: #4caf50;
}

.status-delivered {
    border-left-color: #2196f3;
}

.status-delivered .sent-item-status {
    color: #2196f3;
}

.status-failed {
    border-left-color: #f44336;
}

.status-failed .sent-item-status {
    color: #f44336;
}

.status-sending {
    border-left-color: #ff9800;
}

.status-sending .sent-item-status {
    color: #ff9800;
}

/* Modal Styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.modal-content {
    background: white;
    border-radius: 8px;
    width: 90%;
    max-width: 500px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    display: flex;
    flex-direction: column;
}

.modal-large {
    max-width: 900px;
}

.modal-large .modal-body {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
}

.modal-large .modal-header {
    flex-shrink: 0;
}

.modal-large .modal-footer {
    flex-shrink: 0;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid #e0e0e0;
}

.modal-header h2 {
    margin: 0;
    font-size: 20px;
}

.modal-close {
    background: none;
    border: none;
    font-size: 28px;
    cursor: pointer;
    color: #666;
    padding: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
}

.modal-close:hover {
    color: #000;
}

.modal-body {
    padding: 20px;
}

.account-input-section {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.account-input-section label {
    font-weight: 500;
    font-size: 14px;
}

.account-input {
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    font-family: inherit;
}

.account-input:focus {
    outline: none;
    border-color: #2196F3;
}

.help-text {
    font-size: 12px;
    color: #666;
    margin: 0;
}

.modal-footer {
    padding: 20px 24px;
    border-top: 1px solid #e0e0e0;
    background: white;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    flex-shrink: 0;
}

.modal-footer .modal-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    align-items: center;
    margin-top: 0;
}

.button-secondary {
    background: #757575;
    color: white;
}

.button-secondary:hover:not(:disabled) {
    background: #616161;
}

.qr-section {
    text-align: center;
}

.qr-section h3 {
    margin-top: 0;
    margin-bottom: 15px;
}

.qr-instructions {
    font-size: 14px;
    color: #666;
    text-align: left;
    background: #f5f5f5;
    padding: 15px;
    border-radius: 4px;
    margin-bottom: 20px;
    line-height: 1.6;
}

.qr-code-container {
    display: flex;
    justify-content: center;
    margin: 20px 0;
    padding: 20px;
    background: #f9f9f9;
    border-radius: 8px;
}

.qr-code-image {
    max-width: 100%;
    height: auto;
    border: 2px solid #ddd;
    border-radius: 4px;
}

.qr-status {
    margin: 15px 0;
    font-weight: 500;
}

.status-connecting {
    color: #ff9800;
}

.status-connected {
    color: #4caf50;
}

.status-disconnected {
    color: #f44336;
}

.qr-error {
    padding: 20px;
    text-align: center;
    color: #666;
    font-size: 12px;
}

.qr-error p {
    margin: 5px 0;
}

.loading-section {
    text-align: center;
    padding: 40px 20px;
}

.loading-spinner {
    border: 3px solid #f3f3f3;
    border-top: 3px solid #2196F3;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin: 0 auto 20px;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.loading-section p {
    color: #666;
    font-size: 14px;
    margin: 0;
}

.account-id-display {
    margin-top: 20px;
    padding: 12px;
    background: #f5f5f5;
    border-radius: 4px;
    border: 1px solid #e0e0e0;
    font-size: 14px;
}

.account-id-display strong {
    color: #666;
    margin-right: 8px;
}

.account-id-value {
    color: #2196F3;
    font-family: 'Courier New', monospace;
    font-weight: 600;
    font-size: 13px;
}

.account-status-list {
    margin-bottom: 20px;
    padding: 15px;
    background: #f9f9f9;
    border-radius: 4px;
}

.account-status-list h3 {
    margin: 0 0 15px 0;
    font-size: 16px;
}

.account-status-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(275px, 1fr));
    gap: 10px;
}

.account-status-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    background: white;
    border-radius: 4px;
    border: 1px solid #e0e0e0;
    position: relative;
}

.account-actions {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 5px;
}

.account-menu-container {
    position: relative;
}

.button-icon {
    background: transparent;
    border: 1px solid #ddd;
    color: #666;
    font-size: 18px;
    line-height: 1;
    padding: 4px 8px;
    min-width: auto;
}

.button-icon:hover {
    background: #f5f5f5;
    border-color: #bbb;
}

.button-icon.button-success {
    background: #4caf50 !important;
    color: white !important;
    border-color: #4caf50 !important;
}

.button-icon.button-success:hover:not(:disabled) {
    background: #45a049 !important;
    border-color: #45a049 !important;
    box-shadow: 0 2px 4px rgba(76, 175, 80, 0.3);
}

.button-icon.button-danger {
    background: #f44336 !important;
    color: white !important;
    border-color: #f44336 !important;
}

.button-icon.button-danger:hover:not(:disabled) {
    background: #da190b !important;
    border-color: #da190b !important;
    box-shadow: 0 2px 4px rgba(244, 67, 54, 0.3);
}

.account-menu-dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 4px;
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    min-width: 150px;
    z-index: 1000;
    overflow: hidden;
}

.account-menu-item {
    display: block;
    width: 100%;
    padding: 10px 15px;
    text-align: left;
    background: white;
    border: none;
    cursor: pointer;
    font-size: 14px;
    color: #333;
    transition: background-color 0.2s ease;
}

.account-menu-item:hover {
    background: #f5f5f5;
}

.account-menu-item:first-child {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
}

.account-menu-item:last-child {
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
}

.account-status-badge {
    padding: 4px 8px;
    border-radius: 3px;
    font-size: 12px;
    font-weight: 500;
    text-transform: capitalize;
}

.account-status-badge.status-connected {
    background: #c8e6c9;
    color: #2e7d32;
}

.account-status-badge.status-connecting {
    background: #ffe0b2;
    color: #e65100;
}

.account-status-badge.status-disconnected {
    background: #ffcdd2;
    color: #c62828;
}

.account-display {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.account-name {
    font-weight: 500;
    color: #333;
}

.account-phone {
    font-size: 12px;
    color: #666;
    font-family: 'Courier New', monospace;
}

.account-id-fallback {
    color: #999;
    font-size: 13px;
    font-family: 'Courier New', monospace;
}

.configuration-section {
    background: white;
    border-radius: 8px;
    margin-bottom: 20px;
    border: 1px solid #e0e0e0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    position: relative;
}

.configuration-section-content {
    padding: 20px;
}

.configuration-selector {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
}

.configuration-selector label {
    font-weight: 500;
}

.configuration-selector select {
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    min-width: 200px;
}

.checkbox-label {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 14px;
    white-space: nowrap;
}

.open-configurations {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
    gap: 20px;
}

.configuration-card {
    background: #f9f9f9;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 15px;
}

.configuration-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 10px;
}

.configuration-card-header h3 {
    margin: 0;
    font-size: 18px;
}

.configuration-card-actions {
    display: flex;
    gap: 5px;
}

.configuration-card-content {
    display: flex;
    flex-direction: column;
}

.config-account-groups {
    margin-bottom: 10px;
}

.config-groups-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: #f5f5f5;
    border-radius: 4px;
    cursor: pointer;
    user-select: none;
    border: 1px solid #e0e0e0;
}

.config-groups-header:hover {
    background: #eeeeee;
}

.config-groups-header-left {
    display: flex;
    align-items: center;
    gap: 8px;
}

.config-groups-toggle-icon {
    font-size: 10px;
    color: #666;
    transition: transform 0.2s;
    display: inline-block;
}

.config-groups-toggle-icon.expanded {
    transform: rotate(90deg);
}

.config-groups-header-pills {
    display: flex;
    gap: 6px;
    align-items: center;
}

.access-pill {
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;
}

.access-pill.accessible {
    background: #e8f5e9;
    color: #2e7d32;
}

.access-pill.not-accessible {
    background: #ffebee;
    color: #c62828;
}

.config-groups-content {
    margin-top: 8px;
}

.config-account-groups ul {
    margin: 5px 0;
    padding-left: 20px;
}

.config-tagline-override {
    display: flex;
    flex-direction: column;
    gap: 5px;
    margin-bottom: 0;
}


.config-message-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 10px;
}

.config-message-editor {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.config-message-editor :deep(.tagline-input-wrapper) {
    width: 100%;
}

.config-message-editor-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.config-message-editor label {
    font-weight: 500;
    font-size: 13px;
}

.config-message-textarea {
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-family: inherit;
    font-size: 14px;
    resize: vertical;
    min-height: 120px;
}

.config-message-textarea:focus {
    outline: none;
    border-color: #2196F3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.config-account-groups-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;
}

.config-account-groups-table th,
.config-account-groups-table td {
    padding: 8px 12px;
    text-align: left;
    border-bottom: 1px solid #e0e0e0;
}

.config-account-groups-table th {
    background: #f5f5f5;
    font-weight: 600;
    font-size: 12px;
}

.config-account-groups-table tbody tr:hover {
    background: #f9f9f9;
}

.group-name-jid {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.group-name {
    font-weight: 500;
}

.group-jid {
    font-size: 12px;
    color: #666;
}

.whatsapp-preview-container {
    background: #e5ddd5;
    border-radius: 6px;
    padding: 8px;
    min-height: 50px;
}

.whatsapp-preview-message {
    display: flex;
    justify-content: flex-start;
}

.whatsapp-bubble {
    background: #dcf8c6;
    border-radius: 7.5px;
    padding: 0;
    max-width: 325px;
    word-wrap: break-word;
    font-size: 14.2px;
    line-height: 19px;
    color: #303030;
    box-shadow: 0 1px 0.5px rgba(0, 0, 0, 0.13);
    text-align: left;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding: 6px;

}

.whatsapp-message-text {
    padding: 5px;
}

.whatsapp-bubble:has(.whatsapp-link-preview) .whatsapp-message-text {
    padding-top: 5px;
}

.whatsapp-bubble strong {
    font-weight: 600;
}

.whatsapp-bubble em {
    font-style: italic;
}

.whatsapp-bubble del {
    text-decoration: line-through;
}

.whatsapp-bubble code {
    font-family: 'Courier New', monospace;
    background: rgba(0, 0, 0, 0.05);
    padding: 2px 4px;
    border-radius: 3px;
    font-size: 13px;
}

.whatsapp-bubble a {
    color: #0066cc;
    text-decoration: none;
}

.whatsapp-bubble a:hover {
    text-decoration: underline;
}

.whatsapp-image-preview {
    width: 100%;
    max-width: 100%;
    border-radius: 7.5px 7.5px 0 0;
    overflow: hidden;
    margin-bottom: 0;
    background: #f0f0f0;
}

.whatsapp-image-preview img {
    width: 100%;
    height: auto;
    display: block;
    max-height: 300px;
    object-fit: contain;
    background: #f0f0f0;
}

.whatsapp-message-text.has-image-preview {
    padding-top: 8px;
    border-radius: 0 0 7.5px 7.5px;
}

.global-apply-section {
    display: flex;
    padding: 12px 16px;
    background: #f9f9f9;
    border-radius: 6px;
    margin-bottom: 16px;
    border: 1px solid #e0e0e0;
}

.global-apply-row {
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
    flex-wrap: wrap;
}

.global-apply-item {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 200px;
}

.global-apply-item .rich-text-area {
    flex: 1;
    min-width: 150px;
}

.global-apply-item-media {
    align-items: flex-start;
}

.global-media-wrapper {
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
}

.global-apply-separator {
    width: 1px;
    height: 40px;
    background-color: #ddd;
    flex-shrink: 0;
    align-self: center;
}

.global-media-label {
    font-size: 11px;
    font-weight: 500;
    color: #999;
    margin: 0;
    white-space: nowrap;
}

.global-media-controls {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-direction: row;
}

.global-media-preview-inline {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    background: white;
    border-radius: 4px;
    border: 1px solid #ddd;
}

.global-media-preview-inline .imgPreview {
    display: inline-block;
}

.media-preview-text-inline {
    font-size: 16px;
}

.button-xs {
    padding: 4px 10px;
    font-size: 11px;
    height: 28px;
}

@media (max-width: 768px) {
    .global-apply-row {
        flex-direction: column;
        align-items: stretch;
    }
    
    .global-apply-item {
        min-width: 100%;
    }
    
    .global-apply-separator {
        display: none;
    }
    
    .global-media-controls {
        flex-wrap: wrap;
    }
}

.global-controls-header {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.global-controls-header h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #333;
}

.global-controls-subtitle {
    margin: 0;
    font-size: 13px;
    color: #666;
    font-weight: 400;
}

.global-controls-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
}

@media (max-width: 768px) {
    .global-controls-container {
        grid-template-columns: 1fr;
    }
}

.global-control-section {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 16px;
    background: white;
    border-radius: 6px;
    border: 1px solid #e0e0e0;
}

.global-control-label {
    font-weight: 600;
    font-size: 14px;
    color: #333;
    margin: 0;
}

.global-media-upload {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.global-media-upload-row {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.global-media-preview {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    background: #f9f9f9;
    border-radius: 6px;
    border: 1px solid #e0e0e0;
}

.global-media-preview .imgPreview {
    display: inline-block;
}

.media-preview-text {
    font-size: 13px;
    color: #666;
    font-weight: 500;
}

.global-apply-actions {
    display: flex;
    justify-content: flex-end;
    padding-top: 12px;
    border-top: 1px solid #e0e0e0;
}

/* Legacy support */
.tagline-controls {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px;
    background: #f9f9f9;
    border-radius: 4px;
    margin-bottom: 20px;
    border: 1px solid #e0e0e0;
}

.global-controls-row {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    flex-wrap: wrap;
}

.global-control-item {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
    min-width: 200px;
}

.global-controls-group {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.global-controls-group label {
    font-weight: 500;
    white-space: nowrap;
}

.config-compact-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 8px;
    flex-wrap: wrap;
}

.config-media-upload {
    display: flex;
    flex-direction: column;
    gap: 5px;
    margin-bottom: 15px;
}

.config-media-upload label {
    font-weight: 500;
}

.config-media-upload-compact {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 0 0 auto;
}

.config-media-upload-compact label {
    font-weight: 500;
    font-size: 12px;
    white-space: nowrap;
    margin: 0;
}

.config-media-upload-controls {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.config-header-image-preview {
    display: flex;
    align-items: center;
    height: 20px;
    width: 20px;
    border-radius: 3px;
    overflow: hidden;
    border: 1px solid #ddd;
    background: white;
    flex-shrink: 0;
}

.config-header-image-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.config-media-preview {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    background: #f5f5f5;
    border-radius: 4px;
    border: 1px solid #e0e0e0;
}

.config-media-preview .imgPreview {
    display: inline-block;
}

.config-media-preview-compact {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 2px 6px;
    background: #f5f5f5;
    border-radius: 3px;
    border: 1px solid #e0e0e0;
}

.config-media-preview-compact .imgPreview {
    display: inline-block;
}

.media-preview-text-compact {
    font-size: 16px;
    line-height: 1;
}

.config-tagline-override {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.config-tagline-override-compact {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 1;
    min-width: 200px;
}

.config-tagline-override-compact label {
    font-weight: 500;
    font-size: 12px;
    white-space: nowrap;
    margin: 0;
}

.config-tagline-input {
    flex: 1;
    padding: 4px 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 12px;
    font-family: inherit;
    min-width: 0;
}

.config-tagline-input:focus {
    outline: none;
    border-color: #2196F3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.tagline-controls input {
    flex: 1;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
}

.config-message-preview {
    background: white;
    padding: 10px;
    border-radius: 4px;
    border: 1px solid #e0e0e0;
}

.config-message-preview strong {
    font-size: 12px;
    font-weight: 600;
    color: #666;
    display: block;
    margin-bottom: 6px;
}

.whatsapp-link-preview {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 7.5px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    overflow: hidden;
    display: flex;
    width: 100%;
    margin-bottom: 0;
}

.link-preview-image {
    width: 100px;
    min-width: 100px;
    height: 100px;
    background: #f0f0f0;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.link-preview-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.link-preview-content {
    flex: 1;
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    justify-content: space-between;
}

.link-preview-title {
    font-weight: 600;
    font-size: 13px;
    color: #303030;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
}

.link-preview-description {
    font-size: 12px;
    color: #666;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
}

.link-preview-url {
    font-size: 11px;
    color: #999;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-top: 4px;
}

.message-preview-text {
    white-space: pre-wrap;
    margin-top: 5px;
    padding: 10px;
    background: #f5f5f5;
    border-radius: 4px;
    max-height: 200px;
    overflow-y: auto;
    font-size: 14px;
}

.config-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;
}

.config-schedule-controls {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    width: 100%;
}

.schedule-mode-select {
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    height: 38px;
    box-sizing: border-box;
    background: white;
    cursor: pointer;
}

.schedule-mode-select:focus {
    outline: none;
    border-color: #2196F3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.schedule-datetime-input {
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    height: 38px;
    box-sizing: border-box;
    font-family: inherit;
}

.schedule-datetime-input:focus {
    outline: none;
    border-color: #2196F3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.status-badge {
    padding: 4px 8px;
    border-radius: 3px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
}

.status-badge.status-pending {
    background: #e3f2fd;
    color: #1976d2;
}

.status-badge.status-processing {
    background: #fff3e0;
    color: #f57c00;
}

.status-badge.status-scheduled {
    background: #f3e5f5;
    color: #7b1fa2;
}

.status-badge.status-sent {
    background: #c8e6c9;
    color: #2e7d32;
}

.status-badge.status-delivered {
    background: #bbdefb;
    color: #1565c0;
}

.status-badge.status-failed {
    background: #ffcdd2;
    color: #c62828;
}

.status-badge.status-sending {
    background: #ffe0b2;
    color: #e65100;
}

.status-badge.status-updating {
    background: #e1f5fe;
    color: #0277bd;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.status-update-spinner {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.status-update-spinner-inline {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-left: 6px;
}

.status-text-separator {
    margin: 0 4px;
    opacity: 0.6;
}

.spinner-icon {
    display: inline-block;
    animation: spin 1s linear infinite;
    font-size: 14px;
    line-height: 1;
}

.countdown-text {
    font-weight: 600;
    font-size: 12px;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

/* Scheduled messages table styling - smaller fonts and buttons */
.scheduled-message-row {
    font-size: 12px;
}

.scheduled-message-row td {
    font-size: 12px;
    padding: 8px;
}

.scheduled-cell-small {
    font-size: 12px !important;
}

.scheduled-datetime-wrapper {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.scheduled-datetime-input-group {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    position: relative;
}

.scheduled-datetime-input-small {
    padding: 4px 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 11px;
    height: 28px;
    box-sizing: border-box;
    font-family: inherit;
    width: 165px;
}

.save-datetime-button {
    flex-shrink: 0;
    padding: 4px 10px !important;
    height: 28px !important;
    white-space: nowrap;
    position: relative;
}

.save-button-spinner,
.remove-button-spinner,
.post-button-spinner {
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.save-button-spinner .material-symbols-outlined,
.remove-button-spinner .material-symbols-outlined,
.post-button-spinner .material-symbols-outlined {
    font-size: 16px;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

.scheduled-datetime-input-small:focus {
    outline: none;
    border-color: #2196F3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.countdown-text {
    font-size: 10px;
    font-weight: 600;
    color: #1976d2;
    white-space: nowrap;
    margin-top: 2px;
}

.button-xs {
    padding: 4px 8px;
    font-size: 11px;
    height: 24px;
    line-height: 1.2;
    min-width: auto;
}

.button-xs.button-primary {
    background: #2196F3;
    color: white;
    border: none;
}

.button-xs.button-primary:hover {
    background: #1976d2;
}

.button-xs.button-danger {
    background: #f44336;
    color: white;
    border: none;
}

.button-xs.button-danger:hover {
    background: #d32f2f;
}

.error-message-text {
    font-size: 11px;
    color: #c62828;
    margin-top: 4px;
    font-style: italic;
}

.deal-selection-section {
    background: white;
    border-radius: 8px;
    margin-bottom: 20px;
    border: 1px solid #e0e0e0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.deal-selection-content {
    padding: 20px;
}

.section-disabled {
    opacity: 0.5;
    pointer-events: none;
    position: relative;
}

.section-disabled::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.5);
    z-index: 1;
    border-radius: 8px;
}


.tagline-controls label {
    font-weight: 500;
}

.tagline-controls .tagline-input-wrapper {
    position: relative;
    flex: 1;
    min-width: 200px;
}

.tagline-textarea {
    width: 100%;
    padding: 8px 35px 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    font-family: inherit;
    resize: vertical;
    min-height: 50px;
    box-sizing: border-box;
}

.tagline-textarea:focus {
    outline: none;
    border-color: #2196F3;
    box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.1);
}


.queue-table {
    width: 100%;
    border-collapse: collapse;
}

.queue-table th,
.queue-table td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #ddd;
}

.queue-table th {
    background: #e0e0e0;
    font-weight: 600;
}

.queue-table tbody tr:hover {
    background: #f5f5f5;
}

.message-preview-cell {
    max-width: 400px;
    word-wrap: break-word;
}

.button-warning {
    background: #ff9800;
    color: white;
}

.button-warning:hover:not(:disabled) {
    background: #f57c00;
}

.button-icon-lighter {
    border: 1px solid #ededed;
    color: #666;
}

.config-form {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.form-group label {
    font-weight: 600;
    font-size: 14px;
    color: #333;
}

.form-input,
.form-textarea {
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    font-family: inherit;
    box-sizing: border-box;
    width: 100%;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-input:hover,
.form-textarea:hover {
    border-color: #bbb;
}

.form-input:focus,
.form-textarea:focus {
    outline: none;
    border-color: #2196F3;
    box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.1);
}

.form-textarea {
    resize: vertical;
    min-height: 140px;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    line-height: 1.5;
}

.form-hint {
    font-size: 12px;
    color: #666;
    font-style: italic;
    margin-top: 4px;
    line-height: 1.4;
}

.config-account-selection {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.config-account-selection .header-controls {
    display: flex;
    gap: 12px;
    align-items: flex-end;
    flex-wrap: wrap;
    padding: 15px;
    background: #f9f9f9;
    border-radius: 4px;
    border: 1px solid #e0e0e0;
}

.config-account-selection .dropdown-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
    flex: 1;
    min-width: 200px;
}

.config-account-selection .dropdown-group label {
    font-size: 12px;
    font-weight: 500;
    color: #666;
}

.config-account-selection .dropdown-group select {
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    background: white;
    height: 38px;
    box-sizing: border-box;
}

.config-account-selection .dropdown-group select:focus {
    outline: none;
    border-color: #2196F3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.config-account-selection .dropdown-group select:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
    opacity: 0.6;
}

.config-groups-list {
    margin-top: 15px;
}

.config-groups-list table {
    width: 100%;
}

.config-groups-list .config-account-groups-table th:last-child,
.config-groups-list .config-account-groups-table td:last-child {
    width: 100px;
    text-align: center;
}

.access-indicator {
    display: inline-block;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
}

.access-indicator.accessible {
    background-color: #e8f5e9;
    color: #2e7d32;
}

.access-indicator.not-accessible {
    background-color: #ffebee;
    color: #c62828;
}

.templates-management {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.template-selector-row {
    display: flex;
    align-items: center;
    gap: 10px;
}

.template-dropdown-wrapper {
    flex: 1;
    min-width: 200px;
}

.template-dropdown {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    background: white;
    cursor: pointer;
}

.template-dropdown:focus {
    outline: none;
    border-color: #2196F3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.template-add-btn {
    min-width: 40px;
    padding: 8px 12px;
    font-size: 20px;
    font-weight: bold;
    line-height: 1;
}

.template-editor {
    margin-top: 15px;
    padding: 15px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    background: #fafafa;
}

.template-editor-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 15px;
    margin-bottom: 15px;
}

.template-name-input {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.template-name-input label {
    font-size: 12px;
    font-weight: 500;
    color: #666;
}

.template-name-field {
    width: 100%;
    font-weight: 500;
}

.template-content {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.template-content label {
    font-size: 12px;
    font-weight: 500;
    color: #666;
}

.template-content-field {
    width: 100%;
    resize: vertical;
    min-height: 120px;
}

.no-templates,
.no-template-selected {
    padding: 20px;
    text-align: center;
    background: #f9f9f9;
    border: 1px dashed #ddd;
    border-radius: 6px;
    color: #666;
    margin-top: 15px;
}

.no-templates p,
.no-template-selected p {
    margin: 0 0 15px 0;
}

.default-message-fallback {
    margin-top: 15px;
    padding: 15px;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
}

.default-message-fallback p {
    margin: 0 0 10px 0;
    font-size: 13px;
    color: #666;
}

.default-message-fallback textarea {
    background: #f5f5f5;
    cursor: not-allowed;
}


.template-selector-in-header {
    display: flex;
    align-items: center;
    gap: 10px;
}

.template-selector-in-header select {
    font-size: 11px;
}

</style>
